import { GlobalLocationPermissionGuide } from "@/components/common/GlobalLocationPermissionGuide";
import { Toaster } from "@/components/ui/sonner";
// Toaster do shadcn/radix — necessário para renderizar toasts disparados via
// `useToast()` / `toast()` de `@/hooks/use-toast` (usado em ~72 arquivos do
// projeto, ex.: mensagens de erro de RPC em mutations). Sem este provider
// na árvore, os toasts são despachados mas nunca chegam à UI.
import { Toaster as RadixToaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useParams } from "react-router-dom";
import { ThemeProvider } from "next-themes";
// Inicialização do sistema de internacionalização (i18n)
import '@/i18n';
// Sentry ErrorBoundary para captura global de erros de componentes React
import { SentryErrorBoundary } from "@/lib/sentry";
import { ErrorFallback } from "@/components/ErrorFallback";
import AcceptInvite from "./pages/AcceptInvite";
import { AuthProvider } from "@/hooks/useAuth";
import { NotificationsRealtimeProvider } from "@/providers/NotificationsRealtimeProvider";
// v2026-09-03: feedback global de bloqueios do Workflow (modal, não toast de erro).
import { WorkflowGuardNoticeProvider } from "@/providers/WorkflowGuardNoticeProvider";
// Modais de criação rápida abertos "no lugar" pelo botão "+" do header.
import { QuickCreateModalsProvider } from "@/contexts/QuickCreateModalsContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { TenantProtectedRoute } from "@/components/TenantProtectedRoute";
import OnboardingProtectedRoute from "@/components/OnboardingProtectedRoute";
// =============================================================================
// Módulo Afiliados (Fase 2): Auth/Onboarding isolado do sistema interno
// =============================================================================
import { AffiliateAuthProvider } from "@/hooks/useAffiliateAuth";
import { AffiliateProtectedRoute } from "@/components/affiliate/AffiliateProtectedRoute";
import { AffiliateLayout } from "@/components/affiliate/AffiliateLayout";
import AffiliateLogin from "@/pages/affiliate/AffiliateLogin";
import AffiliateForgotPassword from "@/pages/affiliate/AffiliateForgotPassword";
import AffiliateResetPassword from "@/pages/affiliate/AffiliateResetPassword";
import AffiliateSignup from "@/pages/affiliate/AffiliateSignup";
import AffiliateSignupResolver from "@/pages/affiliate/AffiliateSignupResolver";
// Convite privado (invite_only): resolver descobre o programa via token
// e redireciona para login ou cadastro conforme afiliado existente ou não.
import AffiliateInviteResolver from "@/pages/affiliate/AffiliateInviteResolver";
import AffiliateAcceptInvite from "@/pages/affiliate/AffiliateAcceptInvite";
import AffiliateAwaitingApproval from "@/pages/affiliate/AffiliateAwaitingApproval";
import AffiliateLinkProgram from "@/pages/affiliate/AffiliateLinkProgram";
import AffiliateSelectAccount from "@/pages/affiliate/AffiliateSelectAccount";
import AffiliateRedirectPage from "@/pages/affiliate/AffiliateRedirectPage";
// Fase 3 — Bloco 3A: Painel do Afiliado (shells)
import AffiliateDashboard from "@/pages/affiliate/AffiliateDashboard";
import AffiliateLinks from "@/pages/affiliate/AffiliateLinks";
import AffiliateReferrals from "@/pages/affiliate/AffiliateReferrals";
import AffiliateCommissions from "@/pages/affiliate/AffiliateCommissions";
import AffiliatePayouts from "@/pages/affiliate/AffiliatePayouts";
import AffiliateMaterials from "@/pages/affiliate/AffiliateMaterials";
import AffiliateProfile from "@/pages/affiliate/AffiliateProfile";
// ---- Fase 4: Extranet KB ----
import { ExtranetAuthProviderRoute } from "@/components/kb/extranet/ExtranetAuthProviderRoute";

import { ExtranetProtectedRoute } from "@/components/kb/extranet/ExtranetProtectedRoute";
import { ExtranetLayout } from "@/components/kb/extranet/ExtranetLayout";
import ExtranetLogin from "@/pages/kb/extranet/ExtranetLogin";
import ExtranetSignup from "@/pages/kb/extranet/ExtranetSignup";
import ExtranetForgotPassword from "@/pages/kb/extranet/ExtranetForgotPassword";
import ExtranetResetPassword from "@/pages/kb/extranet/ExtranetResetPassword";
import ExtranetConfirmEmail from "@/pages/kb/extranet/ExtranetConfirmEmail";
import ExtranetAwaitingApproval from "@/pages/kb/extranet/ExtranetAwaitingApproval";
import ExtranetHome from "@/pages/kb/extranet/ExtranetHome";
import ExtranetSpacePage from "@/pages/kb/extranet/ExtranetSpacePage";
import ExtranetPagePage from "@/pages/kb/extranet/ExtranetPagePage";
import ExtranetProfile from "@/pages/kb/extranet/ExtranetProfile";
import ExtranetMyAccesses from "@/pages/kb/extranet/ExtranetMyAccesses";
import { PermissionGuard } from "@/components/PermissionGuard";
import { CrmModuleGuard } from "@/components/CrmModuleGuard";
import { AnyModuleGuard } from "@/components/AnyModuleGuard";
import { AdminLayout } from "@/components/layouts/AdminLayout";
import { TenantLayout } from "@/components/layouts/TenantLayout";
import { AudioPlayerProvider } from "@/contexts/AudioPlayerContext";
import Index from "./pages/Index";
import Auth from "./pages/Auth";
import Pending from "./pages/Pending";
import Onboarding from "./pages/Onboarding";
import AccessDenied from "./pages/AccessDenied";
import SelectContext from "./pages/SelectContext";

import AdminDashboard from "./pages/admin/Dashboard";
import AdminTenants from "./pages/admin/Tenants";
import AdminUsers from "./pages/admin/Users";
import AdminRoles from "./pages/admin/Roles";
import AdminProviders from "./pages/admin/Providers";
import AdminPlans from "./pages/admin/Plans";
import AdminAddons from "./pages/admin/Addons";
import AdminChannelTypes from "./pages/admin/ChannelTypes";
import AdminPlanMatrix from "./pages/admin/PlanMatrix";
import AdminQueueMonitor from "./pages/admin/QueueMonitor";
import AdminCampaignQueueMonitor from "./pages/admin/CampaignQueueMonitor";
import AdminObservability from "./pages/admin/Observability";
import AdminStressTest from "./pages/admin/StressTest";
import AdminCampaignStressTest from "./pages/admin/CampaignStressTest";
import AdminPgmqMonitor from "./pages/admin/PgmqMonitor";
import AdminDlqMonitor from "./pages/admin/DlqMonitor";
import AdminDealImportDlqMonitor from "./pages/admin/DealImportDlqMonitor";
import AdminKbPipelineMonitor from "./pages/admin/KbPipelineMonitor";
import AdminKbAiObservability from "./pages/admin/KbAiObservability";
import AdminProviderInstances from "./pages/admin/ProviderInstances";
import AdminSettings from "./pages/admin/Settings";
import AdminDatabasePerformance from "./pages/admin/DatabasePerformance";
import AdminChatPerformance from "./pages/admin/ChatPerformance";
import AdminAddonManagement from "./pages/admin/AddonManagement";
import AdminWahaMonitor from "./pages/admin/WahaMonitor";
import AdminTenantLimitsAudit from "./pages/admin/TenantLimitsAudit";
import AdminWebhookQueueMonitor from "./pages/admin/WebhookQueueMonitor";
import AdminWebhookRawIngestMonitor from "./pages/admin/WebhookRawIngestMonitor";
import AdminContractQueueMonitor from "./pages/admin/ContractQueueMonitor";
import AdminFinancePage from "./pages/admin/AdminFinancePage";
import AdminFinancialQueueMonitor from "./pages/admin/FinancialQueueMonitor";
import TenantDashboard from "./pages/tenant/Dashboard";
import TenantChat from "./pages/tenant/Chat";
// KB (Base de Conhecimento) — F1 Sprint 2 · Bloco 3/3
import KbSpacesPage from "./pages/kb/KbSpacesPage";
import KbSpaceDetailPage from "./pages/kb/KbSpaceDetailPage";
import KbImportPage from "./pages/kb/KbImportPage";
import KbPageEditorPage from "./pages/kb/KbPageEditorPage";
import KbPermissionsPage from "./pages/kb/KbPermissionsPage";
import KbExtranetAdminPage from "./pages/kb/KbExtranetAdminPage";
import { KnowledgeProtectedRoute } from "./components/kb/KnowledgeProtectedRoute";
// KB Wiki pública (F3 Bloco A) — SEO + rotas anônimas
import KbPublicTenantPage from "./pages/kb/public/KbPublicTenantPage";
import KbPublicSpacePage from "./pages/kb/public/KbPublicSpacePage";
import KbPublicPagePage from "./pages/kb/public/KbPublicPagePage";
import TenantInboxes from "./pages/tenant/Inboxes";

/**
 * Redirect de compatibilidade: a antiga página de detalhes da inbox virou modal.
 * Converte /tenant/inboxes/:id em /tenant/inboxes?inboxId=:id
 */
function InboxDetailsRedirect() {
  const { id } = useParams<{ id: string }>();
  return <Navigate to={`/tenant/inboxes?inboxId=${id ?? ''}`} replace />;
}

import TenantContacts from "./pages/tenant/Contacts";
import TenantDepartments from "./pages/tenant/Departments";
import TenantRoutingRules from "./pages/tenant/RoutingRules";
import TenantAgents from "./pages/tenant/Agents";
import TenantCompany from "./pages/tenant/Company";
import TenantSettings from "./pages/tenant/Settings";

import TenantFlows from "./pages/tenant/Flows";
import TenantFlowBuilder from "./pages/tenant/FlowBuilder";
import TenantFlowTheme from "./pages/tenant/FlowTheme";
import TenantFlowShare from "./pages/tenant/FlowShare";
import TenantFlowResults from "./pages/tenant/FlowResults";
import TenantCampaigns from "./pages/tenant/Campaigns";
import TenantCampaignDetails from "./pages/tenant/CampaignDetails";
import TenantKanban from "./pages/tenant/Kanban";
import TenantServiceQueue from "./pages/tenant/ServiceQueue";
import TenantTemplates from "./pages/tenant/Templates";
import TenantLists from "./pages/tenant/Lists";
import TenantPlans from "./pages/tenant/PlansRouter";
import AdminTenantManagement from "./pages/admin/TenantManagement";
import TenantDistributionMonitor from "./pages/tenant/DistributionMonitor";
import TenantDistributionQueues from "./pages/tenant/DistributionQueues";
import TenantScheduling from "./pages/tenant/Scheduling";
import TenantCalendar from "./pages/tenant/Calendar";
import TenantCalendarSettings from "./pages/tenant/CalendarSettings";
import PublicFlowChat from "./pages/PublicFlowChat";
import PublicScheduling from "./pages/PublicScheduling";
import PublicSchedulingCancel from "./pages/PublicSchedulingCancel";
import CheckoutPublic from "./pages/CheckoutPublic";
import VerifyAcceptancePage from "./pages/public/VerifyAcceptancePage";
import VerifyChecklistReportPage from "./pages/public/VerifyChecklistReportPage";
import PublicProposal from "./pages/PublicProposal";
import PublicInspectionPage from "./pages/public/PublicInspectionPage";
import ProviderInspectionPage from "./pages/provider-portal/ProviderInspectionPage";
import StudioPublicFrame from "./pages/studio/StudioPublicFrame";
import Install from "./pages/Install";
import NotFound from "./pages/NotFound";
// =============================================================================
// Painel Admin White Label (/wl) - Fase 1
// =============================================================================
import { WlPermissionsProvider } from "@/contexts/WlPermissionsContext";
import { WhiteLabelProtectedRoute } from "@/components/wl/WhiteLabelProtectedRoute";
import { WLPermissionGuard } from "@/components/wl/WLPermissionGuard";
import { WhiteLabelLayout } from "@/layouts/WhiteLabelLayout";
import { SupportSessionBanner } from "@/components/wl/SupportSessionBanner";
// Atalho "Minhas tarefas" integrado ao avatar do cabeçalho
import { TasksQuickProvider } from "@/contexts/TasksQuickContext";
import WlDashboard from "./pages/wl/WlDashboard";
import WlOperators from "./pages/wl/WlOperators";
import WlRoles from "./pages/wl/WlRoles";
import WlTenants from "./pages/wl/WlTenants";
import WlTenantUsers from "./pages/wl/WlTenantUsers";
import WlInstances from "./pages/wl/WlInstances";
import WlFinancial from "./pages/wl/WlFinancial";
import WlAudit from "./pages/wl/WlAudit";
import WlSupportSessions from "./pages/wl/WlSupportSessions";
import AdminSupportSessions from "./pages/admin/AdminSupportSessions";
import WlPlans from "./pages/wl/WlPlans";
import WlAddons from "./pages/wl/WlAddons";
import WlPlanMatrix from "./pages/wl/WlPlanMatrix";
import { WlPlaceholderPage } from "./pages/wl/WlPlaceholderPage";
import WlSettings from "./pages/wl/WlSettings";
// CRM Module - Fase 1, 2 e 3
import { CrmLayout } from "@/components/layouts/CrmLayout";
import { ModularLayout } from "@/components/layouts/ModularLayout";
import { SupportWidget } from "@/components/SupportWidget";
import CrmDashboard from "./pages/crm/CrmDashboard";
import CrmWorkspaces from "./pages/crm/CrmWorkspaces";
import CrmPipelines from "./pages/crm/CrmPipelines";
import CrmPlaybooks from "./pages/crm/CrmPlaybooks";
import ActivitiesList from "./pages/activities/ActivitiesList";
import ActivitiesDashboard from "./pages/activities/ActivitiesDashboard";
import { ActivitiesLayout } from "@/components/layouts/ActivitiesLayout";
import CrmPlaybookEditor from "./pages/crm/CrmPlaybookEditor";
import CrmProducts from "./pages/crm/CrmProducts";
import CrmPriceTables from "./pages/crm/CrmPriceTables";
import CrmProductCategories from "./pages/crm/CrmProductCategories";
import CrmLeadSources from "./pages/crm/CrmLeadSources";
import CrmLossReasons from "./pages/crm/CrmLossReasons";
import CrmProposalTypes from "./pages/crm/CrmProposalTypes";
import CrmDealsPipeline from "./pages/crm/CrmDealsPipeline";
import CrmDeals from "./pages/crm/CrmDeals";
import CrmDealsImport from "./pages/crm/CrmDealsImport";
import CrmDealDetails from "./pages/crm/CrmDealDetails";
import CrmSellers from "./pages/crm/CrmSellers";
import CrmCustomers from "./pages/crm/CrmCustomers";
import CrmGoals from "./pages/crm/CrmGoals";
import CrmActivities from "./pages/crm/CrmActivities";
import CrmActivitiesCalendar from "./pages/crm/CrmActivitiesCalendar";
import DashboardPermissions from "./pages/crm/DashboardPermissions";
import CrmContractTemplates from "./pages/crm/CrmContractTemplates";
import CrmContracts from "./pages/crm/CrmContracts";
import CrmGeneratedContracts from "./pages/crm/CrmGeneratedContracts";
// Fase 1 do módulo Propostas: Kanban de Propostas Geradas
import CrmProposalsKanban from "./pages/crm/CrmProposalsKanban";
// Listagem de Propostas Geradas (Fase 2 do módulo Propostas)
import CrmProposalsList from "./pages/crm/CrmProposalsList";
import CrmDocumentTypes from "./pages/crm/CrmDocumentTypes";
// Módulo Financeiro
import { FinancialLayout } from "@/components/layouts/FinancialLayout";
import { FinancialModuleGuard } from "@/components/FinancialModuleGuard";
import FinancialAccounts from "./pages/financial/FinancialAccounts";
import FinancialPaymentMethods from "./pages/financial/FinancialPaymentMethods";
import FinancialCharges from "./pages/financial/FinancialCharges";
import FinancialInstallments from "./pages/financial/FinancialInstallments";
import FinancialSubscriptions from "./pages/financial/FinancialSubscriptions";
import FinancialDashboard from "./pages/financial/FinancialDashboard";
import FinancialPaymentLinks from "./pages/financial/FinancialPaymentLinks";
import FinancialBillingReminders from "./pages/financial/FinancialBillingReminders";
// Central Unificada de Lembretes
import RemindersCenter from "./pages/tenant/RemindersCenter";
// Central de Mensagens Agendadas (chat)
import ScheduledMessagesPage from "./pages/tenant/ScheduledMessagesPage";
// Módulo Tarefas
import { TasksLayout } from "@/components/layouts/TasksLayout";
import { TasksModuleGuard } from "@/components/TasksModuleGuard";
import TasksDashboard from "./pages/tasks/TasksDashboard";
import TasksDefaultRedirect from "./components/tasks/TasksDefaultRedirect";
import TaskCategories from "./pages/tasks/TaskCategories";
// Módulo Veículos (Lista + Categorias) — visível apenas com vehicles_enabled
import VehiclesListPage from "./pages/vehicles/VehiclesListPage";
import VehiclesDashboardPage from "./pages/vehicles/VehiclesDashboardPage";
import VehicleInspectionsPage from "./pages/vehicles/VehicleInspectionsPage";
import VehicleCategoriesPage from "./pages/vehicles/VehicleCategoriesPage";
// 2026-10-06: cadastro único de Pessoas (Fase 1 do Estoque)
import PersonsPage from "./pages/persons/PersonsPage";
import ShipmentsPage from "./pages/logistics/ShipmentsPage";
import ShipmentsKanbanPage from "./pages/logistics/ShipmentsKanbanPage";
import { PersonsRoleGuard } from "./pages/persons/PersonsRoleGuard";
// 2026-10-06: Fase 2 do Estoque
import StockLocationsPage from "./pages/stock/StockLocationsPage";
import TrackerModelsPage from "./pages/stock/TrackerModelsPage";
import StockHomologationsPage from "./pages/stock/StockHomologationsPage";
import StockDataPlansPage from "./pages/stock/StockDataPlansPage";
import StockChipCancellationsPage from "./pages/stock/StockChipCancellationsPage";
import TrackingPlatformsPage from "./pages/stock/TrackingPlatformsPage";
import { ProviderPortalActivate, ProviderPortalHome, ProviderPortalLogin } from "./pages/provider-portal/ProviderPortalPages";
import StockItemsPage from "./pages/stock/StockItemsPage";
import StockMovementsPage from "./pages/stock/StockMovementsPage";
import StockOverviewPage from "./pages/stock/StockOverviewPage";
import { StockModuleGuard } from "./components/stock/StockModuleGuard";
import { ServiceOrderModuleGuard } from "./components/service-orders/ServiceOrderModuleGuard";
import ServiceOrdersPlaceholderPage from "./pages/service-orders/ServiceOrdersPlaceholderPage";
import ServiceTypesPage from "./pages/service-orders/ServiceTypesPage";
import ServiceOrdersDashboardPage from "./pages/service-orders/ServiceOrdersDashboardPage";
import ServiceOrdersListPage from "./pages/service-orders/ServiceOrdersListPage";
import ServiceOrdersKanbanPage from "./pages/service-orders/ServiceOrdersKanbanPage";
import VehicleStatusesPage from "./pages/vehicles/VehicleStatusesPage";
// Módulo Workflow (motor de orquestração 2.0 — em construção)
import { WorkflowModuleGuard } from "@/components/WorkflowModuleGuard";
import WorkflowHomePage from "./pages/workflow/WorkflowHomePage";
import WorkflowBuilderPage from "./pages/workflow/WorkflowBuilderPage";
import WorkflowPendingPage from "./pages/workflow/WorkflowPendingPage";
import WorkflowExecutionsPage from "./pages/workflow/WorkflowExecutionsPage";
import WorkflowErrorsPage from "./pages/workflow/WorkflowErrorsPage";

// Módulo Inteligência de Dados — Integration Hub (Fase 1)
import DataConnectionsPage from "./pages/data/Connections";
// Página de detalhe de um fornecedor do catálogo de integrações (Hub 1.1)
import DataProviderPage from "./pages/data/ProviderPage";
// Dados Externos (Fase 2): datasets importados e histórico de sincronização
import DataDatasetsPage from "./pages/data/Datasets";
import DataExplorerPage from "./pages/data/DatasetExplorer";
import DataSyncHistoryPage from "./pages/data/SyncHistory";
// Fase 4 — acompanhamento dos lotes de ação sobre dados externos
import DataActionBatchesPage from "./pages/data/ActionBatches";
// Mapa de Veículos (teste de embed em iframe do sistema do fornecedor)
import VehicleMapPage from "./pages/vehicles/VehicleMapPage";
import TaskPhases from "./pages/tasks/TaskPhases";
import TaskClassifications from "./pages/tasks/TaskClassifications";
import TaskCompletionReasons from "./pages/tasks/TaskCompletionReasons";
import TaskChecklistTemplatesManager from "./pages/tasks/TaskChecklistTemplatesManager";
import TaskKanbanPage from "./pages/tasks/TaskKanbanPage";
import TaskList from "./pages/tasks/TaskList";
// Central unificada de Permissões (Tabs com Perfis + Dashboards + Fases Tarefas)
import TenantPermissions from "./pages/tenant/Permissions";
// Auditoria de exportações de dados (Configurações › Exportação Dados)
import DataExports from "./pages/tenant/DataExports";
// Templates do Sistema (Configurações › Templates do Sistema)
import SystemTemplates from "./pages/tenant/SystemTemplates";
// Módulo Checklist (Template de Checklist) — 2026-09-26
import ChecklistTemplatesPage from "./pages/checklist/ChecklistTemplatesPage";
import ChecklistTemplateEditorPage from "./pages/checklist/ChecklistTemplateEditorPage";
// Content Studio — modelos de documentos (Conteúdo › Modelos)
import StudioTemplates from "./pages/tenant/StudioTemplates";
import StudioTemplateEditor from "./pages/tenant/StudioTemplateEditor";
// Módulo Indicação (Fase 5 — Bloco 5A)
import { ReferralLayout } from "@/components/layouts/ReferralLayout";
import { ReferralModuleGuard } from "@/components/ReferralModuleGuard";
import ReferralDashboard from "./pages/referral/ReferralDashboard";
import ReferralPrograms from "./pages/referral/ReferralPrograms";
import ReferralProgramDetail from "./pages/referral/ReferralProgramDetail";
import ReferralCampaigns from "./pages/referral/ReferralCampaigns";
// Removido: ReferralSettings — campos migrados para o modal de Programa.
import ReferralAffiliates from "./pages/referral/ReferralAffiliates";
import ReferralMaterials from "./pages/referral/ReferralMaterials";
import ReferralCommissions from "./pages/referral/ReferralCommissions";
import ReferralPayouts from "./pages/referral/ReferralPayouts";
import ReferralAuditLog from "./pages/referral/ReferralAuditLog";
import ReferralReferralsAdmin from "./pages/referral/ReferralReferralsAdmin";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

const App = () => (
  <SentryErrorBoundary fallback={({ error, resetError }) => <ErrorFallback error={error} resetError={resetError} />}>
    <QueryClientProvider client={queryClient}>
      <ThemeProvider attribute="class" defaultTheme="light" enableSystem>
        <AuthProvider>
          <TooltipProvider>
            <WorkflowGuardNoticeProvider>
            <Toaster />
            <GlobalLocationPermissionGuide />
            <RadixToaster />
        <BrowserRouter>
          <TasksQuickProvider>
          {/* Banner global de Sessão de Suporte WL — visível em qualquer rota */}
          <SupportSessionBanner />
          {/*
           * Provider ÚNICO da subscription Realtime de agent_notifications.
           * Vive dentro do BrowserRouter (usa useNavigate) e do AuthProvider
           * (usa useAuth). Garante 1 canal por sessão + toasts/entrega
           * disparados uma única vez para todos os consumidores do hook.
           */}
          <NotificationsRealtimeProvider>
          {/*
           * Provider dos modais de criação rápida: permite que o botão "+"
           * do header abra Contato/Cliente/Negócio/Tarefa/Evento sobre a
           * tela atual, sem navegar e sem perder o contexto do usuário.
           */}
          <QuickCreateModalsProvider>
          <Routes>
            <Route path="/" element={<Index />} />
            <Route path="/auth" element={<Auth />} />
            <Route path="/pending" element={<Pending />} />
            {/* Tela de escolha entre painel WL e painel do tenant (cruzamento) */}
            <Route path="/select-context" element={<SelectContext />} />
            {/* Página de Acesso Negado - dentro do TenantLayout para manter navegação */}

            <Route
              path="/access-denied"
              element={
                <TenantProtectedRoute>
                  <AudioPlayerProvider>
                    <TenantLayout>
                      <AccessDenied />
                    </TenantLayout>
                  </AudioPlayerProvider>
                </TenantProtectedRoute>
              }
            />
            <Route path="/onboarding" element={
              <OnboardingProtectedRoute>
                <Onboarding />
              </OnboardingProtectedRoute>
            } />
            <Route path="/invite/:token" element={<AcceptInvite />} />
            <Route path="/install" element={<Install />} />

            {/* KB — Wiki pública (sem autenticação). F3 Bloco A. */}
            <Route path="/kb/:tenantSlug" element={<KbPublicTenantPage />} />
            <Route path="/kb/:tenantSlug/:spaceSlug" element={<KbPublicSpacePage />} />
            <Route
              path="/kb/:tenantSlug/:spaceSlug/:pageSlug"
              element={<KbPublicPagePage />}
            />


            
            {/* Admin Routes */}
            <Route
              path="/admin"
              element={
                <ProtectedRoute requireRootAdmin>
                  <AdminLayout>
                    <AdminDashboard />
                  </AdminLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/tenants"
              element={
                <ProtectedRoute requireRootAdmin>
                  <AdminLayout>
                    <AdminTenants />
                  </AdminLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/tenant-limits"
              element={
                <ProtectedRoute requireRootAdmin>
                  <AdminLayout>
                    <AdminTenantLimitsAudit />
                  </AdminLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/users"
              element={
                <ProtectedRoute requireRootAdmin>
                  <AdminLayout>
                    <AdminUsers />
                  </AdminLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/roles"
              element={
                <ProtectedRoute requireRootAdmin>
                  <AdminLayout>
                    <AdminRoles />
                  </AdminLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/providers"
              element={
                <ProtectedRoute requireRootAdmin>
                  <AdminLayout>
                    <AdminProviders />
                  </AdminLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/plans"
              element={
                <ProtectedRoute requireRootAdmin>
                  <AdminLayout>
                    <AdminPlans />
                  </AdminLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/addons"
              element={
                <ProtectedRoute requireRootAdmin>
                  <AdminLayout>
                    <AdminAddons />
                  </AdminLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/channel-types"
              element={
                <ProtectedRoute requireRootAdmin>
                  <AdminLayout>
                    <AdminChannelTypes />
                  </AdminLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/plan-matrix"
              element={
                <ProtectedRoute requireRootAdmin>
                  <AdminLayout>
                    <AdminPlanMatrix />
                  </AdminLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/queue"
              element={
                <ProtectedRoute requireRootAdmin>
                  <AdminLayout>
                    <AdminQueueMonitor />
                  </AdminLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/campaign-queue"
              element={
                <ProtectedRoute requireRootAdmin>
                  <AdminLayout>
                    <AdminCampaignQueueMonitor />
                  </AdminLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/observability"
              element={
                <ProtectedRoute requireRootAdmin>
                  <AdminLayout>
                    <AdminObservability />
                  </AdminLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/stress-test"
              element={
                <ProtectedRoute requireRootAdmin>
                  <AdminLayout>
                    <AdminStressTest />
                  </AdminLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/campaign-stress-test"
              element={
                <ProtectedRoute requireRootAdmin>
                  <AdminLayout>
                    <AdminCampaignStressTest />
                  </AdminLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/pgmq"
              element={
                <ProtectedRoute requireRootAdmin>
                  <AdminLayout>
                    <AdminPgmqMonitor />
                  </AdminLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/dlq"
              element={
                <ProtectedRoute requireRootAdmin>
                  <AdminLayout>
                    <AdminDlqMonitor />
                  </AdminLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/sessions"
              element={
                <ProtectedRoute requireRootAdmin>
                  <AdminLayout>
                    <AdminSupportSessions />
                  </AdminLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/deal-import-dlq"
              element={
                <ProtectedRoute requireRootAdmin>
                  <AdminLayout>
                    <AdminDealImportDlqMonitor />
                  </AdminLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/kb-pipeline"
              element={
                <ProtectedRoute requireRootAdmin>
                  <AdminLayout>
                    <AdminKbPipelineMonitor />
                  </AdminLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/kb-ai-observability"
              element={
                <ProtectedRoute requireRootAdmin>
                  <AdminLayout>
                    <AdminKbAiObservability />
                  </AdminLayout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin/provider-instances"
              element={
                <ProtectedRoute requireRootAdmin>
                  <AdminLayout>
                    <AdminProviderInstances />
                  </AdminLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/settings"
              element={
                <ProtectedRoute requireRootAdmin>
                  <AdminLayout>
                    <AdminSettings />
                  </AdminLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/db-performance"
              element={
                <ProtectedRoute requireRootAdmin>
                  <AdminLayout>
                    <AdminDatabasePerformance />
                  </AdminLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/addon-management"
              element={
                <ProtectedRoute requireRootAdmin>
                  <AdminLayout>
                    <AdminAddonManagement />
                  </AdminLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/waha-monitor"
              element={
                <ProtectedRoute requireRootAdmin>
                  <AdminLayout>
                    <AdminWahaMonitor />
                  </AdminLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/chat-performance"
              element={
                <ProtectedRoute requireRootAdmin>
                  <AdminLayout>
                    <AdminChatPerformance />
                  </AdminLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/tenant-management"
              element={
                <ProtectedRoute requireRootAdmin>
                  <AdminLayout>
                    <AdminTenantManagement />
                  </AdminLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/webhook-queue"
              element={
                <ProtectedRoute requireRootAdmin>
                  <AdminLayout>
                    <AdminWebhookQueueMonitor />
                  </AdminLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/raw-ingest"
              element={
                <ProtectedRoute requireRootAdmin>
                  <AdminLayout>
                    <AdminWebhookRawIngestMonitor />
                  </AdminLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/contract-queue"
              element={
                <ProtectedRoute requireRootAdmin>
                  <AdminLayout>
                    <AdminContractQueueMonitor />
                  </AdminLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/financial"
              element={
                <ProtectedRoute requireRootAdmin>
                  <AdminLayout>
                    <AdminFinancePage />
                  </AdminLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/financial-queue"
              element={
                <ProtectedRoute requireRootAdmin>
                  <AdminLayout>
                    <AdminFinancialQueueMonitor />
                  </AdminLayout>
                </ProtectedRoute>
              }
            />

            {/* =========================================================
                 White Label Admin Routes (/wl) — Fase 1
                 RBAC isolado via WlPermissionsProvider + WLPermissionGuard
                ========================================================= */}
            <Route
              path="/wl"
              element={
                <ProtectedRoute>
                  <WlPermissionsProvider>
                    <WhiteLabelProtectedRoute>
                      <WLPermissionGuard menuKey="wl.dashboard">
                        <WhiteLabelLayout><WlDashboard /></WhiteLabelLayout>
                      </WLPermissionGuard>
                    </WhiteLabelProtectedRoute>
                  </WlPermissionsProvider>
                </ProtectedRoute>
              }
            />
            <Route
              path="/wl/operators"
              element={
                <ProtectedRoute>
                  <WlPermissionsProvider>
                    <WhiteLabelProtectedRoute>
                      <WLPermissionGuard menuKey="wl.wl_users">
                        <WhiteLabelLayout><WlOperators /></WhiteLabelLayout>
                      </WLPermissionGuard>
                    </WhiteLabelProtectedRoute>
                  </WlPermissionsProvider>
                </ProtectedRoute>
              }
            />
            <Route
              path="/wl/roles"
              element={
                <ProtectedRoute>
                  <WlPermissionsProvider>
                    <WhiteLabelProtectedRoute>
                      <WLPermissionGuard menuKey="wl.wl_roles">
                        <WhiteLabelLayout><WlRoles /></WhiteLabelLayout>
                      </WLPermissionGuard>
                    </WhiteLabelProtectedRoute>
                  </WlPermissionsProvider>
                </ProtectedRoute>
              }
            />
            {/* Placeholders das próximas fases (Tenants, Instâncias, Usuários dos Tenants, Financeiro, Auditoria, Settings) */}
            <Route path="/wl/tenants" element={
              <ProtectedRoute><WlPermissionsProvider><WhiteLabelProtectedRoute>
                <WLPermissionGuard menuKey="wl.tenants">
                  <WhiteLabelLayout><WlTenants /></WhiteLabelLayout>
                </WLPermissionGuard>
              </WhiteLabelProtectedRoute></WlPermissionsProvider></ProtectedRoute>
            } />
            <Route path="/wl/instances" element={
              <ProtectedRoute><WlPermissionsProvider><WhiteLabelProtectedRoute>
                <WLPermissionGuard menuKey="wl.instances">
                  <WhiteLabelLayout><WlInstances /></WhiteLabelLayout>
                </WLPermissionGuard>
              </WhiteLabelProtectedRoute></WlPermissionsProvider></ProtectedRoute>
            } />
            <Route path="/wl/tenant-users" element={
              <ProtectedRoute><WlPermissionsProvider><WhiteLabelProtectedRoute>
                <WLPermissionGuard menuKey="wl.tenant_users">
                  <WhiteLabelLayout><WlTenantUsers /></WhiteLabelLayout>
                </WLPermissionGuard>
              </WhiteLabelProtectedRoute></WlPermissionsProvider></ProtectedRoute>
            } />
            <Route path="/wl/financial" element={
              <ProtectedRoute><WlPermissionsProvider><WhiteLabelProtectedRoute>
                <WLPermissionGuard menuKey="wl.financial">
                  <WhiteLabelLayout><WlFinancial /></WhiteLabelLayout>
                </WLPermissionGuard>
              </WhiteLabelProtectedRoute></WlPermissionsProvider></ProtectedRoute>
            } />
            <Route path="/wl/audit" element={
              <ProtectedRoute><WlPermissionsProvider><WhiteLabelProtectedRoute>
                <WLPermissionGuard menuKey="wl.audit">
                  <WhiteLabelLayout><WlAudit /></WhiteLabelLayout>
                </WLPermissionGuard>
              </WhiteLabelProtectedRoute></WlPermissionsProvider></ProtectedRoute>
            } />
            <Route path="/wl/sessions" element={
              <ProtectedRoute><WlPermissionsProvider><WhiteLabelProtectedRoute>
                <WLPermissionGuard menuKey="wl.audit">
                  <WhiteLabelLayout><WlSupportSessions /></WhiteLabelLayout>
                </WLPermissionGuard>
              </WhiteLabelProtectedRoute></WlPermissionsProvider></ProtectedRoute>
            } />
            <Route path="/wl/plans" element={
              <ProtectedRoute><WlPermissionsProvider><WhiteLabelProtectedRoute>
                <WLPermissionGuard menuKey="wl.plans">
                  <WhiteLabelLayout><WlPlans /></WhiteLabelLayout>
                </WLPermissionGuard>
              </WhiteLabelProtectedRoute></WlPermissionsProvider></ProtectedRoute>
            } />
            <Route path="/wl/addons" element={
              <ProtectedRoute><WlPermissionsProvider><WhiteLabelProtectedRoute>
                <WLPermissionGuard menuKey="wl.addons">
                  <WhiteLabelLayout><WlAddons /></WhiteLabelLayout>
                </WLPermissionGuard>
              </WhiteLabelProtectedRoute></WlPermissionsProvider></ProtectedRoute>
            } />
            <Route path="/wl/plan-matrix" element={
              <ProtectedRoute><WlPermissionsProvider><WhiteLabelProtectedRoute>
                <WLPermissionGuard menuKey="wl.plan_matrix">
                  <WhiteLabelLayout><WlPlanMatrix /></WhiteLabelLayout>
                </WLPermissionGuard>
              </WhiteLabelProtectedRoute></WlPermissionsProvider></ProtectedRoute>
            } />
            <Route path="/wl/settings" element={
              <ProtectedRoute><WlPermissionsProvider><WhiteLabelProtectedRoute>
                <WLPermissionGuard menuKey="wl.settings">
                  <WhiteLabelLayout><WlSettings /></WhiteLabelLayout>
                </WLPermissionGuard>
              </WhiteLabelProtectedRoute></WlPermissionsProvider></ProtectedRoute>
            } />
            {/* Redirects de compatibilidade: rotas antigas viraram abas internas de /wl/settings */}
            <Route path="/wl/settings/internal-tenant" element={<Navigate to="/wl/settings?tab=tenant-interno" replace />} />
            <Route path="/wl/settings/crm-integration" element={<Navigate to="/wl/settings?tab=crm-integration" replace />} />


            {/* Tenant Routes - Envolvidos com AudioPlayerProvider e PermissionGuard */}
            <Route
              path="/tenant"
              element={
                <TenantProtectedRoute>
                  <AudioPlayerProvider>
                    <PermissionGuard menuKey="dashboard">
                      <TenantLayout>
                        <TenantDashboard />
                      </TenantLayout>
                    </PermissionGuard>
                  </AudioPlayerProvider>
                </TenantProtectedRoute>
              }
            />
            <Route
              path="/tenant/chat"
              element={
                <TenantProtectedRoute>
                  <AudioPlayerProvider>
                    <PermissionGuard menuKey="chat">
                      <TenantLayout>
                        <TenantChat />
                      </TenantLayout>
                    </PermissionGuard>
                  </AudioPlayerProvider>
                </TenantProtectedRoute>
              }
            />
            <Route
              path="/tenant/inboxes"
              element={
                <TenantProtectedRoute>
                  <AudioPlayerProvider>
                    <PermissionGuard menuKey="inboxes">
                      <TenantLayout>
                        <TenantInboxes />
                      </TenantLayout>
                    </PermissionGuard>
                  </AudioPlayerProvider>
                </TenantProtectedRoute>
              }
            />
            {/* Detalhes da Inbox: agora em modal. Mantém deep links antigos redirecionando
                para a listagem com o parâmetro que abre o modal automaticamente. */}
            <Route
              path="/tenant/inboxes/:id"
              element={<InboxDetailsRedirect />}
            />
            <Route
              path="/tenant/contacts"
              element={
                <TenantProtectedRoute>
                  <AudioPlayerProvider>
                    <PermissionGuard menuKey="contacts">
                      <ModularLayout>
                        <TenantContacts />
                      </ModularLayout>
                    </PermissionGuard>
                  </AudioPlayerProvider>
                </TenantProtectedRoute>
              }
            />
            <Route
              path="/tenant/departments"
              element={
                <TenantProtectedRoute>
                  <AudioPlayerProvider>
                    <PermissionGuard menuKey="departments">
                      <TenantLayout>
                        <TenantDepartments />
                      </TenantLayout>
                    </PermissionGuard>
                  </AudioPlayerProvider>
                </TenantProtectedRoute>
              }
            />
            <Route
              path="/tenant/routing"
              element={
                <TenantProtectedRoute>
                  <AudioPlayerProvider>
                    <PermissionGuard menuKey="routing">
                      <TenantLayout>
                        <TenantRoutingRules />
                      </TenantLayout>
                    </PermissionGuard>
                  </AudioPlayerProvider>
                </TenantProtectedRoute>
              }
            />
            <Route
              path="/tenant/agents"
              element={
                <TenantProtectedRoute>
                  <AudioPlayerProvider>
                    <PermissionGuard menuKey="agents">
                      <TenantLayout>
                        <TenantAgents />
                      </TenantLayout>
                    </PermissionGuard>
                  </AudioPlayerProvider>
                </TenantProtectedRoute>
              }
            />
            <Route
              path="/tenant/company"
              element={
                <TenantProtectedRoute>
                  <AudioPlayerProvider>
                    <PermissionGuard menuKey="company" requireAdmin>
                      <TenantLayout>
                        <TenantCompany />
                      </TenantLayout>
                    </PermissionGuard>
                  </AudioPlayerProvider>
                </TenantProtectedRoute>
              }
            />
            {/* Rota compartilhada - usa ModularLayout para manter sidebar do módulo ativo */}
            <Route
              path="/tenant/settings"
              element={
                <TenantProtectedRoute>
                  <AudioPlayerProvider>
                    <PermissionGuard menuKey="settings" requireAdmin>
                      <ModularLayout>
                        <TenantSettings />
                      </ModularLayout>
                    </PermissionGuard>
                  </AudioPlayerProvider>
                </TenantProtectedRoute>
              }
            />



            {/* Rota compartilhada - usa ModularLayout para manter sidebar do módulo ativo */}
            <Route
              path="/tenant/flows"
              element={
                <TenantProtectedRoute>
                  <AudioPlayerProvider>
                    <PermissionGuard menuKey="flows">
                      <ModularLayout>
                        <TenantFlows />
                      </ModularLayout>
                    </PermissionGuard>
                  </AudioPlayerProvider>
                </TenantProtectedRoute>
              }
            />
            <Route
              path="/tenant/flows/:flowId"
              element={
                <TenantProtectedRoute>
                  <AudioPlayerProvider>
                    <PermissionGuard menuKey="flows">
                      <TenantFlowBuilder />
                    </PermissionGuard>
                  </AudioPlayerProvider>
                </TenantProtectedRoute>
              }
            />
            <Route
              path="/tenant/flows/:flowId/theme"
              element={
                <TenantProtectedRoute>
                  <AudioPlayerProvider>
                    <PermissionGuard menuKey="flows">
                      <TenantFlowTheme />
                    </PermissionGuard>
                  </AudioPlayerProvider>
                </TenantProtectedRoute>
              }
            />
            <Route
              path="/tenant/flows/:flowId/share"
              element={
                <TenantProtectedRoute>
                  <AudioPlayerProvider>
                    <PermissionGuard menuKey="flows">
                      <TenantFlowShare />
                    </PermissionGuard>
                  </AudioPlayerProvider>
                </TenantProtectedRoute>
              }
            />
            <Route
              path="/tenant/flows/:flowId/results"
              element={
                <TenantProtectedRoute>
                  <AudioPlayerProvider>
                    <PermissionGuard menuKey="flows">
                      <TenantFlowResults />
                    </PermissionGuard>
                  </AudioPlayerProvider>
                </TenantProtectedRoute>
              }
            />
            <Route
              path="/tenant/campaigns"
              element={
                <TenantProtectedRoute>
                  <AudioPlayerProvider>
                    <PermissionGuard menuKey="campaigns">
                      <TenantLayout>
                        <TenantCampaigns />
                      </TenantLayout>
                    </PermissionGuard>
                  </AudioPlayerProvider>
                </TenantProtectedRoute>
              }
            />
            <Route
              path="/tenant/campaigns/:id"
              element={
                <TenantProtectedRoute>
                  <AudioPlayerProvider>
                    <PermissionGuard menuKey="campaigns">
                      <TenantLayout>
                        <TenantCampaignDetails />
                      </TenantLayout>
                    </PermissionGuard>
                  </AudioPlayerProvider>
                </TenantProtectedRoute>
              }
            />
            <Route
              path="/tenant/kanban"
              element={
                <TenantProtectedRoute>
                  <AudioPlayerProvider>
                    <PermissionGuard menuKey="kanban">
                      <TenantLayout>
                        <TenantKanban />
                      </TenantLayout>
                    </PermissionGuard>
                  </AudioPlayerProvider>
                </TenantProtectedRoute>
              }
            />
            {/* KB — Base de Conhecimento (F1 Sprint 2 + F1-Fix guard) */}
            <Route
              path="/tenant/kb"
              element={
                <TenantProtectedRoute>
                  <KnowledgeProtectedRoute>
                    <AudioPlayerProvider>
                      <TenantLayout>
                        <KbSpacesPage />
                      </TenantLayout>
                    </AudioPlayerProvider>
                  </KnowledgeProtectedRoute>
                </TenantProtectedRoute>
              }
            />
            <Route
              path="/tenant/kb/import"
              element={
                <TenantProtectedRoute>
                  <KnowledgeProtectedRoute>
                    <AudioPlayerProvider>
                      <TenantLayout>
                        <KbImportPage />
                      </TenantLayout>
                    </AudioPlayerProvider>
                  </KnowledgeProtectedRoute>
                </TenantProtectedRoute>
              }
            />
            <Route
              path="/tenant/kb/permissions"
              element={
                <TenantProtectedRoute>
                  <KnowledgeProtectedRoute>
                    <AudioPlayerProvider>
                      <TenantLayout>
                        <KbPermissionsPage />
                      </TenantLayout>
                    </AudioPlayerProvider>
                  </KnowledgeProtectedRoute>
                </TenantProtectedRoute>
              }
            />
            <Route
              path="/tenant/kb/extranet"
              element={
                <TenantProtectedRoute>
                  <KnowledgeProtectedRoute>
                    <AudioPlayerProvider>
                      <TenantLayout>
                        <KbExtranetAdminPage />
                      </TenantLayout>
                    </AudioPlayerProvider>
                  </KnowledgeProtectedRoute>
                </TenantProtectedRoute>
              }
            />

            <Route
              path="/tenant/kb/:spaceId"
              element={
                <TenantProtectedRoute>
                  <KnowledgeProtectedRoute>
                    <AudioPlayerProvider>
                      <TenantLayout>
                        <KbSpaceDetailPage />
                      </TenantLayout>
                    </AudioPlayerProvider>
                  </KnowledgeProtectedRoute>
                </TenantProtectedRoute>
              }
            />
            <Route
              path="/tenant/kb/:spaceId/pages/:pageId"
              element={
                <TenantProtectedRoute>
                  <KnowledgeProtectedRoute>
                    <AudioPlayerProvider>
                      <TenantLayout fullHeight>
                        <KbPageEditorPage />
                      </TenantLayout>
                    </AudioPlayerProvider>
                  </KnowledgeProtectedRoute>
                </TenantProtectedRoute>
              }
            />

            <Route
              path="/tenant/queue"
              element={
                <TenantProtectedRoute>
                  <AudioPlayerProvider>
                    <PermissionGuard menuKey="queue">
                      <TenantLayout>
                        <TenantServiceQueue />
                      </TenantLayout>
                    </PermissionGuard>
                  </AudioPlayerProvider>
                </TenantProtectedRoute>
              }
            />
            {/*
              Templates: protegido pela menuKey dedicada `templates` (v1.0.0).
              O backfill libera a permissão nos perfis existentes para não
              causar regressão em quem já usava a tela.
            */}
            <Route
              path="/tenant/templates"
              element={
                <TenantProtectedRoute>
                  <AudioPlayerProvider>
                    <PermissionGuard menuKey="templates">
                      <TenantLayout>
                        <TenantTemplates />
                      </TenantLayout>
                    </PermissionGuard>
                  </AudioPlayerProvider>
                </TenantProtectedRoute>
              }
            />
            <Route
              path="/tenant/lists"
              element={
                <TenantProtectedRoute>
                  <AudioPlayerProvider>
                    <PermissionGuard menuKey="campaigns">
                      <ModularLayout>
                        <TenantLists />
                      </ModularLayout>
                    </PermissionGuard>
                  </AudioPlayerProvider>
                </TenantProtectedRoute>
              }
            />
            <Route
              path="/tenant/plans"
              element={
                <TenantProtectedRoute>
                  <AudioPlayerProvider>
                    <PermissionGuard menuKey="company" requireAdmin>
                      <TenantLayout>
                        <TenantPlans />
                      </TenantLayout>
                    </PermissionGuard>
                  </AudioPlayerProvider>
                </TenantProtectedRoute>
              }
            />
            {/* v1.3.0 - Rotas com permissões separadas */}
            <Route
              path="/tenant/distribution"
              element={
                <TenantProtectedRoute>
                  <AudioPlayerProvider>
                    <PermissionGuard menuKey="distribution">
                      <ModularLayout>
                        <TenantDistributionMonitor />
                      </ModularLayout>
                    </PermissionGuard>
                  </AudioPlayerProvider>
                </TenantProtectedRoute>
              }
            />
            <Route
              path="/tenant/distribution/queues"
              element={
                <TenantProtectedRoute>
                  <AudioPlayerProvider>
                    <PermissionGuard menuKey="distribution_queues">
                      <ModularLayout>
                        <TenantDistributionQueues />
                      </ModularLayout>
                    </PermissionGuard>
                  </AudioPlayerProvider>
                </TenantProtectedRoute>
              }
            />
            
            {/* ========== CRM Routes - Módulo de Negócios ========== */}
            <Route
              path="/tenant/crm"
              element={
                <TenantProtectedRoute>
                  <CrmModuleGuard>
                  <AudioPlayerProvider>
                    <PermissionGuard menuKey="crm_dashboard">
                      <CrmLayout>
                        <CrmDashboard />
                      </CrmLayout>
                    </PermissionGuard>
                  </AudioPlayerProvider>
                  </CrmModuleGuard>
                </TenantProtectedRoute>
              }
            />
            <Route
              path="/tenant/crm/workspaces"
              element={
                <TenantProtectedRoute>
                  <CrmModuleGuard>
                  <AudioPlayerProvider>
                    <PermissionGuard menuKey="crm_workspaces">
                      {/* Workspaces é rota COMPARTILHADA (CRM + Atividades).
                          Use ModularLayout para que a sidebar siga o `activeModule`.
                          Ver SHARED_ROUTES em src/hooks/useActiveModule.ts */}
                      <ModularLayout>
                        <CrmWorkspaces />
                      </ModularLayout>
                    </PermissionGuard>
                  </AudioPlayerProvider>
                  </CrmModuleGuard>
                </TenantProtectedRoute>
              }
            />
            {/* ============================================================
                CENTRAL UNIFICADA DE PERMISSÕES (v1.0.0)
                Substitui /tenant/crm/dashboard-permissions e
                /tenant/tasks/phase-permissions (mantidos como redirect abaixo).
                Acessível a partir das sidebars de TODOS os módulos via
                ModularLayout (a sidebar é escolhida pelo módulo ativo).
                ============================================================ */}
            <Route
              path="/tenant/permissions"
              element={
                <TenantProtectedRoute>
                  <AudioPlayerProvider>
                    <PermissionGuard menuKey="permissions" requireAdmin>
                      <ModularLayout>
                        <TenantPermissions />
                      </ModularLayout>
                    </PermissionGuard>
                  </AudioPlayerProvider>
                </TenantProtectedRoute>
              }
             />
            {/* ============================================================
                EXPORTAÇÃO DADOS (auditoria) — somente administradores.
                Lista tudo que foi exportado: quem, quando, filtros e colunas.
                ============================================================ */}
            <Route
              path="/tenant/settings/exports"
              element={
                <TenantProtectedRoute>
                  <AudioPlayerProvider>
                    <PermissionGuard menuKey="settings" requireAdmin>
                      <ModularLayout>
                        <DataExports />
                      </ModularLayout>
                    </PermissionGuard>
                  </AudioPlayerProvider>
                </TenantProtectedRoute>
              }
            />

            {/* ============================================================
                TEMPLATES DO SISTEMA — somente administradores.
                Exporta a configuração da conta e aplica em outra conta.
                ============================================================ */}
            <Route
              path="/tenant/settings/system-templates"
              element={
                <TenantProtectedRoute>
                  <AudioPlayerProvider>
                    <PermissionGuard menuKey="settings" requireAdmin>
                      <ModularLayout>
                        <SystemTemplates />
                      </ModularLayout>
                    </PermissionGuard>
                  </AudioPlayerProvider>
                </TenantProtectedRoute>
              }
            />

            {/* TEMPLATE DE CHECKLIST — Configurações › Template de Checklist */}
            <Route path="/tenant/settings/checklist-templates" element={
              <TenantProtectedRoute><AudioPlayerProvider>
                <PermissionGuard menuKey="checklist_templates"><ModularLayout><ChecklistTemplatesPage /></ModularLayout></PermissionGuard>
              </AudioPlayerProvider></TenantProtectedRoute>
            } />
            <Route path="/tenant/settings/checklist-templates/:id" element={
              <TenantProtectedRoute><AudioPlayerProvider>
                <PermissionGuard menuKey="checklist_templates"><ModularLayout><ChecklistTemplateEditorPage /></ModularLayout></PermissionGuard>
              </AudioPlayerProvider></TenantProtectedRoute>
            } />

            {/* ============================================================
                CONTENT STUDIO — modelos de documentos (propostas, páginas,
                e-mails). Construtor visual por blocos, com pré-visualização
                e publicação. Somente administradores da conta.
                ============================================================ */}
            <Route
              path="/tenant/content/templates"
              element={
                <TenantProtectedRoute>
                  <AudioPlayerProvider>
                    <PermissionGuard menuKey="settings" requireAdmin>
                      <ModularLayout>
                        <StudioTemplates />
                      </ModularLayout>
                    </PermissionGuard>
                  </AudioPlayerProvider>
                </TenantProtectedRoute>
              }
            />
            <Route
              path="/tenant/content/templates/:templateId"
              element={
                <TenantProtectedRoute>
                  <AudioPlayerProvider>
                    <PermissionGuard menuKey="settings" requireAdmin>
                      <ModularLayout>
                        <StudioTemplateEditor />
                      </ModularLayout>
                    </PermissionGuard>
                  </AudioPlayerProvider>
                </TenantProtectedRoute>
              }
            />


            {/* Redirect 301-like: rota antiga preserva bookmarks/links internos. */}
            <Route
              path="/tenant/crm/dashboard-permissions"
              element={<Navigate to="/tenant/permissions?tab=dashboards" replace />}
            />

            <Route
              path="/tenant/crm/pipelines"
              element={
                <TenantProtectedRoute>
                  <CrmModuleGuard>
                  <AudioPlayerProvider>
                    <PermissionGuard menuKey="crm_workspaces">
                      <CrmLayout>
                        <CrmPipelines />
                      </CrmLayout>
                    </PermissionGuard>
                  </AudioPlayerProvider>
                  </CrmModuleGuard>
                </TenantProtectedRoute>
              }
            />
            <Route
              path="/tenant/crm/playbooks"
              element={
                <TenantProtectedRoute>
                  <CrmModuleGuard>
                  <AudioPlayerProvider>
                    <PermissionGuard menuKey="crm_playbooks">
                      <ModularLayout>
                        <CrmPlaybooks />
                      </ModularLayout>
                    </PermissionGuard>
                  </AudioPlayerProvider>
                  </CrmModuleGuard>
                </TenantProtectedRoute>
              }
            />
            <Route
              path="/tenant/crm/playbooks/:id"
              element={
                <TenantProtectedRoute>
                  <CrmModuleGuard>
                  <AudioPlayerProvider>
                    <PermissionGuard menuKey="crm_playbooks">
                      <ModularLayout>
                        <CrmPlaybookEditor />
                      </ModularLayout>
                    </PermissionGuard>
                  </AudioPlayerProvider>
                  </CrmModuleGuard>
                </TenantProtectedRoute>
              }
            />
            {/* Fase 3 — Módulo "Atividades" (item 3.11)
                Padronizado sob /tenant/activities/* (alinhado aos demais módulos).
                Layout dedicado + sidebar própria + guard por menu_key 'activities_list'. */}
            <Route
              path="/tenant/activities/list"
              element={
                <TenantProtectedRoute>
                  <CrmModuleGuard>
                    <AudioPlayerProvider>
                      <PermissionGuard menuKey="activities_list">
                        <ModularLayout>
                          <ActivitiesList />
                        </ModularLayout>
                      </PermissionGuard>
                    </AudioPlayerProvider>
                  </CrmModuleGuard>
                </TenantProtectedRoute>
              }
            />
            {/* Dashboard de Atividades — usa o sistema unificado de dashboards */}
            <Route
              path="/tenant/activities/dashboard"
              element={
                <TenantProtectedRoute>
                  <CrmModuleGuard>
                    <AudioPlayerProvider>
                      <PermissionGuard menuKey="activities_list">
                        <ModularLayout>
                          <ActivitiesDashboard />
                        </ModularLayout>
                      </PermissionGuard>
                    </AudioPlayerProvider>
                  </CrmModuleGuard>
                </TenantProtectedRoute>
              }
            />
            {/* /tenant/activities -> dashboard default */}
            <Route
              path="/tenant/activities"
              element={<Navigate to="/tenant/activities/dashboard" replace />}
            />
            {/* Redirects legados de /activities/* para /tenant/activities/*
                (preserva bookmarks e notificações antigas no banco). */}
            <Route
              path="/activities/list"
              element={<Navigate to="/tenant/activities/list" replace />}
            />
            <Route
              path="/activities/dashboard"
              element={<Navigate to="/tenant/activities/dashboard" replace />}
            />
            <Route
              path="/activities"
              element={<Navigate to="/tenant/activities/dashboard" replace />}
            />
            <Route
              path="/tenant/crm/categories"
              element={
                <TenantProtectedRoute>
                  <CrmModuleGuard>
                  <AudioPlayerProvider>
                    <PermissionGuard menuKey="crm_categories">
                      <ModularLayout>
                        <CrmProductCategories />
                      </ModularLayout>
                    </PermissionGuard>
                  </AudioPlayerProvider>
                  </CrmModuleGuard>
                </TenantProtectedRoute>
              }
            />
            <Route
              path="/tenant/crm/lead-sources"
              element={
                <TenantProtectedRoute>
                  <CrmModuleGuard>
                  <AudioPlayerProvider>
                    <PermissionGuard menuKey="crm_param_lead_sources">
                      <ModularLayout>
                        <CrmLeadSources />
                      </ModularLayout>
                    </PermissionGuard>
                  </AudioPlayerProvider>
                  </CrmModuleGuard>
                </TenantProtectedRoute>
              }
            />
            <Route
              path="/tenant/crm/loss-reasons"
              element={
                <TenantProtectedRoute>
                  <CrmModuleGuard>
                  <AudioPlayerProvider>
                    <PermissionGuard menuKey="crm_param_loss_reasons">
                      <ModularLayout>
                        <CrmLossReasons />
                      </ModularLayout>
                    </PermissionGuard>
                  </AudioPlayerProvider>
                  </CrmModuleGuard>
                </TenantProtectedRoute>
              }
            />
            {/* Parâmetro CRM: Tipos de Proposta (padrão "Nova venda" por tenant) */}
            <Route
              path="/tenant/crm/proposal-types"
              element={
                <TenantProtectedRoute>
                  <CrmModuleGuard>
                  <AudioPlayerProvider>
                    <PermissionGuard menuKey="crm_param_proposal_types">
                      <ModularLayout>
                        <CrmProposalTypes />
                      </ModularLayout>
                    </PermissionGuard>
                  </AudioPlayerProvider>
                  </CrmModuleGuard>
                </TenantProtectedRoute>
              }
            />
            <Route
              path="/tenant/crm/pipeline"
              element={
                <TenantProtectedRoute>
                  <CrmModuleGuard>
                  <AudioPlayerProvider>
                    <PermissionGuard menuKey="crm_deals">
                      <CrmLayout>
                        <CrmDealsPipeline />
                      </CrmLayout>
                    </PermissionGuard>
                  </AudioPlayerProvider>
                  </CrmModuleGuard>
                </TenantProtectedRoute>
              }
            />
            <Route
              path="/tenant/crm/products"
              element={
                <TenantProtectedRoute>
                  <CrmModuleGuard>
                  <AudioPlayerProvider>
                    <PermissionGuard menuKey="crm_products">
                      <ModularLayout>
                        <CrmProducts />
                      </ModularLayout>
                    </PermissionGuard>
                  </AudioPlayerProvider>
                  </CrmModuleGuard>
                </TenantProtectedRoute>
              }
            />
            <Route
              path="/tenant/crm/price-tables"
              element={
                <TenantProtectedRoute>
                  <CrmModuleGuard>
                  <AudioPlayerProvider>
                    <PermissionGuard menuKey="crm_prices">
                      <ModularLayout>
                        <CrmPriceTables />
                      </ModularLayout>
                    </PermissionGuard>
                  </AudioPlayerProvider>
                  </CrmModuleGuard>
                </TenantProtectedRoute>
              }
            />
            <Route
              path="/tenant/crm/sellers"
              element={
                <TenantProtectedRoute>
                  <CrmModuleGuard>
                  <AudioPlayerProvider>
                    <PermissionGuard menuKey="crm_sellers">
                      <CrmLayout>
                        <CrmSellers />
                      </CrmLayout>
                    </PermissionGuard>
                  </AudioPlayerProvider>
                  </CrmModuleGuard>
                </TenantProtectedRoute>
              }
            />
            <Route
              path="/tenant/crm/deals"
              element={
                <TenantProtectedRoute>
                  <CrmModuleGuard>
                  <AudioPlayerProvider>
                    <PermissionGuard menuKey="crm_deals">
                      <ModularLayout>
                        <CrmDeals />
                      </ModularLayout>
                    </PermissionGuard>
                  </AudioPlayerProvider>
                  </CrmModuleGuard>
                </TenantProtectedRoute>
              }
            />
            <Route
              path="/tenant/crm/deals/import"
              element={
                <TenantProtectedRoute>
                  <CrmModuleGuard>
                    <PermissionGuard menuKey="crm_deals_import">
                      <ModularLayout>
                        <CrmDealsImport />
                      </ModularLayout>
                    </PermissionGuard>
                  </CrmModuleGuard>
                </TenantProtectedRoute>
              }
            />
            <Route
              path="/tenant/crm/deals/:id"
              element={
                <TenantProtectedRoute>
                  <CrmModuleGuard>
                  <AudioPlayerProvider>
                    <PermissionGuard menuKey="crm_deals">
                      <ModularLayout>
                        <CrmDealDetails />
                      </ModularLayout>
                    </PermissionGuard>
                  </AudioPlayerProvider>
                  </CrmModuleGuard>
              </TenantProtectedRoute>
              }
            />
            <Route
              path="/tenant/crm/customers"
              element={
                <TenantProtectedRoute>
                  {/* Clientes é tela compartilhada: basta UM dos módulos
                      (CRM, Financeiro, Retenção, Jurídico ou Veículos) */}
                  <AnyModuleGuard
                    anyOf={[
                      'crm_enabled',
                      'financial_enabled',
                      'retention_enabled',
                      'legal_enabled',
                      'vehicles_enabled',
                    ]}
                  >
                  <AudioPlayerProvider>
                    <PermissionGuard menuKey="crm_customers">
                      <ModularLayout>
                        <CrmCustomers />
                      </ModularLayout>
                    </PermissionGuard>
                  </AudioPlayerProvider>
                  </AnyModuleGuard>
                </TenantProtectedRoute>
              }
            />
            <Route
              path="/tenant/crm/goals"
              element={
                <TenantProtectedRoute>
                  <CrmModuleGuard>
                  <AudioPlayerProvider>
                    <PermissionGuard menuKey="crm_goals">
                      <CrmLayout>
                        <CrmGoals />
                      </CrmLayout>
                    </PermissionGuard>
                  </AudioPlayerProvider>
                  </CrmModuleGuard>
                </TenantProtectedRoute>
              }
            />
            <Route
              path="/tenant/crm/activities"
              element={
                <TenantProtectedRoute>
                  <CrmModuleGuard>
                  <AudioPlayerProvider>
                    <PermissionGuard menuKey="crm_deals">
                      <ModularLayout>
                        <CrmActivities />
                      </ModularLayout>
                    </PermissionGuard>
                  </AudioPlayerProvider>
                  </CrmModuleGuard>
                </TenantProtectedRoute>
              }
            />
            <Route
              path="/tenant/crm/activities/calendar"
              element={
                <TenantProtectedRoute>
                  <CrmModuleGuard>
                  <AudioPlayerProvider>
                    <PermissionGuard menuKey="crm_deals">
                      <ModularLayout>
                        <CrmActivitiesCalendar />
                      </ModularLayout>
                    </PermissionGuard>
                  </AudioPlayerProvider>
                  </CrmModuleGuard>
                </TenantProtectedRoute>
              }
            />
            
            {/* Rota de Agendamento Online - configuração admin */}
            <Route
              path="/tenant/scheduling"
              element={
                <TenantProtectedRoute>
                  <CrmModuleGuard>
                  <AudioPlayerProvider>
                    <PermissionGuard menuKey="scheduling">
                      <ModularLayout>
                        <TenantScheduling />
                      </ModularLayout>
                    </PermissionGuard>
                  </AudioPlayerProvider>
                  </CrmModuleGuard>
                </TenantProtectedRoute>
              }
            />

            {/* Calendário Global - Fase 2 (sync read-only) */}
            <Route
              path="/tenant/calendar"
              element={
                <TenantProtectedRoute>
                  <TenantCalendar />
                </TenantProtectedRoute>
              }
            />

            {/* Configurações central de Calendários (CRUD + compartilhamento + integrações) */}
            <Route
              path="/tenant/calendar/settings"
              element={
                <TenantProtectedRoute>
                  <TenantCalendarSettings />
                </TenantProtectedRoute>
              }
            />

            {/* Central Unificada de Lembretes (todas as réguas em um só lugar) */}
            <Route
              path="/tenant/reminders"
              element={
                <TenantProtectedRoute>
                  <AudioPlayerProvider>
                    <ModularLayout>
                      <PermissionGuard menuKey="reminders_center">
                        <RemindersCenter />
                      </PermissionGuard>
                    </ModularLayout>
                  </AudioPlayerProvider>
                </TenantProtectedRoute>
              }
            />

            {/* Central de Mensagens Agendadas (envios programados no chat) */}
            <Route
              path="/tenant/scheduled-messages"
              element={
                <TenantProtectedRoute>
                  <AudioPlayerProvider>
                    <ModularLayout>
                      <PermissionGuard menuKey="scheduled_messages">
                        <ScheduledMessagesPage />
                      </PermissionGuard>
                    </ModularLayout>
                  </AudioPlayerProvider>
                </TenantProtectedRoute>
              }
            />
            

            
            
            {/* Rota de Contratos Publicados - CRUD de contratos configurados */}
            <Route
              path="/tenant/crm/contracts"
              element={
                <TenantProtectedRoute>
                  <CrmModuleGuard>
                  <AudioPlayerProvider>
                    <PermissionGuard menuKey="crm_contracts">
                      <CrmLayout>
                        <CrmContracts />
                      </CrmLayout>
                    </PermissionGuard>
                  </AudioPlayerProvider>
                  </CrmModuleGuard>
                </TenantProtectedRoute>
              }
            />

            {/* Rota de Contratos Gerados - Listagem paginada de contratos */}
            <Route
              path="/tenant/crm/generated-contracts"
              element={
                <TenantProtectedRoute>
                  <CrmModuleGuard>
                  <AudioPlayerProvider>
                    <PermissionGuard menuKey="crm_contracts">
                      <ModularLayout>
                        <CrmGeneratedContracts />
                      </ModularLayout>
                    </PermissionGuard>
                  </AudioPlayerProvider>
                  </CrmModuleGuard>
                </TenantProtectedRoute>
              }
            />

            {/* Rota do Kanban de Propostas - Colunas fixas por situação */}
            <Route
              path="/tenant/crm/proposals/kanban"
              element={
                <TenantProtectedRoute>
                  <CrmModuleGuard>
                  <AudioPlayerProvider>
                    <PermissionGuard menuKey="crm_proposals_kanban">
                      <ModularLayout>
                        <CrmProposalsKanban />
                      </ModularLayout>
                    </PermissionGuard>
                  </AudioPlayerProvider>
                  </CrmModuleGuard>
                </TenantProtectedRoute>
              }
            />

            {/* Rota da Listagem de Propostas Geradas (Fase 2) */}
            <Route
              path="/tenant/crm/proposals"
              element={
                <TenantProtectedRoute>
                  <CrmModuleGuard>
                  <AudioPlayerProvider>
                    <PermissionGuard menuKey="crm_proposals_list">
                      <ModularLayout>
                        <CrmProposalsList />
                      </ModularLayout>
                    </PermissionGuard>
                  </AudioPlayerProvider>
                  </CrmModuleGuard>
                </TenantProtectedRoute>
              }
            />



            {/* Rota de Tipos de Documento - Cadastro global de tipos */}
            <Route
              path="/tenant/crm/document-types"
              element={
                <TenantProtectedRoute>
                  <CrmModuleGuard>
                  <AudioPlayerProvider>
                    <PermissionGuard menuKey="crm_document_types">
                      <CrmLayout>
                        <CrmDocumentTypes />
                      </CrmLayout>
                    </PermissionGuard>
                  </AudioPlayerProvider>
                  </CrmModuleGuard>
                </TenantProtectedRoute>
              }
            />

            {/* Rota de Modelos de Contrato - CRUD de templates */}
            <Route
              path="/tenant/crm/contract-templates"
              element={
                <TenantProtectedRoute>
                  <CrmModuleGuard>
                  <AudioPlayerProvider>
                    <PermissionGuard menuKey="crm_contract_templates">
                      <CrmLayout>
                        <CrmContractTemplates />
                      </CrmLayout>
                    </PermissionGuard>
                  </AudioPlayerProvider>
                  </CrmModuleGuard>
                </TenantProtectedRoute>
              }
            />

            {/* ========== Financial Routes - Módulo Financeiro ========== */}
            <Route path="/tenant/financial/accounts" element={
              <TenantProtectedRoute><FinancialModuleGuard><AudioPlayerProvider>
                <PermissionGuard menuKey="financial_accounts"><FinancialLayout><FinancialAccounts /></FinancialLayout></PermissionGuard>
              </AudioPlayerProvider></FinancialModuleGuard></TenantProtectedRoute>
            } />
            <Route path="/tenant/financial/payment-methods" element={
              <TenantProtectedRoute><FinancialModuleGuard><AudioPlayerProvider>
                <PermissionGuard menuKey="financial_payment_methods"><FinancialLayout><FinancialPaymentMethods /></FinancialLayout></PermissionGuard>
              </AudioPlayerProvider></FinancialModuleGuard></TenantProtectedRoute>
            } />
            <Route path="/tenant/financial/charges" element={
              <TenantProtectedRoute><FinancialModuleGuard><AudioPlayerProvider>
                <PermissionGuard menuKey="financial_charges"><FinancialLayout><FinancialCharges /></FinancialLayout></PermissionGuard>
              </AudioPlayerProvider></FinancialModuleGuard></TenantProtectedRoute>
            } />
            <Route path="/tenant/financial/billing-reminders" element={
              <TenantProtectedRoute><FinancialModuleGuard><AudioPlayerProvider>
                <PermissionGuard menuKey="financial_billing_reminders"><FinancialLayout><FinancialBillingReminders /></FinancialLayout></PermissionGuard>
              </AudioPlayerProvider></FinancialModuleGuard></TenantProtectedRoute>
            } />
            <Route path="/tenant/financial/installments" element={
              <TenantProtectedRoute><FinancialModuleGuard><AudioPlayerProvider>
                <PermissionGuard menuKey="financial_charges"><FinancialLayout><FinancialInstallments /></FinancialLayout></PermissionGuard>
              </AudioPlayerProvider></FinancialModuleGuard></TenantProtectedRoute>
            } />
            <Route path="/tenant/financial/subscriptions" element={
              <TenantProtectedRoute><FinancialModuleGuard><AudioPlayerProvider>
                <PermissionGuard menuKey="financial_charges"><FinancialLayout><FinancialSubscriptions /></FinancialLayout></PermissionGuard>
              </AudioPlayerProvider></FinancialModuleGuard></TenantProtectedRoute>
            } />
            {/* Link de Pagamento */}
            <Route path="/tenant/financial/payment-links" element={
              <TenantProtectedRoute><FinancialModuleGuard><AudioPlayerProvider>
                <PermissionGuard menuKey="financial_charges"><FinancialLayout><FinancialPaymentLinks /></FinancialLayout></PermissionGuard>
              </AudioPlayerProvider></FinancialModuleGuard></TenantProtectedRoute>
            } />
            {/* Dashboard Financeiro */}
            <Route path="/tenant/financial/dashboard" element={
              <TenantProtectedRoute><FinancialModuleGuard><AudioPlayerProvider>
                <PermissionGuard menuKey="financial_dashboard"><FinancialLayout><FinancialDashboard /></FinancialLayout></PermissionGuard>
              </AudioPlayerProvider></FinancialModuleGuard></TenantProtectedRoute>
            } />
            {/* Rota default do financeiro redireciona para dashboard */}
            <Route path="/tenant/financial" element={
              <TenantProtectedRoute><FinancialModuleGuard><AudioPlayerProvider>
                <FinancialLayout><FinancialDashboard /></FinancialLayout>
              </AudioPlayerProvider></FinancialModuleGuard></TenantProtectedRoute>
            } />

            {/* ========== MÓDULO TAREFAS ========== */}
            {/* Rota do dashboard de tarefas */}
            <Route path="/tenant/tasks/dashboard" element={
              <TenantProtectedRoute><TasksModuleGuard><AudioPlayerProvider>
                <PermissionGuard menuKey="tasks_dashboard"><TasksLayout><TasksDashboard /></TasksLayout></PermissionGuard>
              </AudioPlayerProvider></TasksModuleGuard></TenantProtectedRoute>
            } />
            <Route path="/tenant/tasks/categories" element={
              <TenantProtectedRoute><TasksModuleGuard><AudioPlayerProvider>
                <PermissionGuard menuKey="tasks_categories"><TasksLayout><TaskCategories /></TasksLayout></PermissionGuard>
              </AudioPlayerProvider></TasksModuleGuard></TenantProtectedRoute>
            } />
            <Route path="/tenant/tasks/phases" element={
              <TenantProtectedRoute><TasksModuleGuard><AudioPlayerProvider>
                <PermissionGuard menuKey="tasks_phases"><TasksLayout><TaskPhases /></TasksLayout></PermissionGuard>
              </AudioPlayerProvider></TasksModuleGuard></TenantProtectedRoute>
            } />
            <Route path="/tenant/tasks/classifications" element={
              <TenantProtectedRoute><TasksModuleGuard><AudioPlayerProvider>
                <PermissionGuard menuKey="tasks_classifications"><TasksLayout><TaskClassifications /></TasksLayout></PermissionGuard>
              </AudioPlayerProvider></TasksModuleGuard></TenantProtectedRoute>
            } />
            {/* Templates de Checklist (Fase 3) — reaproveita permissão de cadastros */}
            <Route path="/tenant/tasks/checklist-templates" element={
              <TenantProtectedRoute><TasksModuleGuard><AudioPlayerProvider>
                <PermissionGuard menuKey="tasks_checklist_templates"><TasksLayout><TaskChecklistTemplatesManager /></TasksLayout></PermissionGuard>
              </AudioPlayerProvider></TasksModuleGuard></TenantProtectedRoute>
            } />
            {/* Kanban de Tarefas */}
            <Route path="/tenant/tasks/kanban" element={
              <TenantProtectedRoute><TasksModuleGuard><AudioPlayerProvider>
                <PermissionGuard menuKey="tasks_kanban"><TasksLayout><TaskKanbanPage /></TasksLayout></PermissionGuard>
              </AudioPlayerProvider></TasksModuleGuard></TenantProtectedRoute>
            } />
            {/* Listagem de Tarefas */}
            <Route path="/tenant/tasks/list" element={
              <TenantProtectedRoute><TasksModuleGuard><AudioPlayerProvider>
                <PermissionGuard menuKey="tasks_list"><TasksLayout><TaskList /></TasksLayout></PermissionGuard>
              </AudioPlayerProvider></TasksModuleGuard></TenantProtectedRoute>
            } />
            {/* Redirect 301-like para central unificada (preserva bookmarks). */}
            <Route
              path="/tenant/tasks/phase-permissions"
              element={<Navigate to="/tenant/permissions?tab=task-phases" replace />}
            />

            {/* Rota default do módulo tarefas - redireciona para primeiro menu permitido */}
            <Route path="/tenant/tasks" element={
              <TenantProtectedRoute><TasksModuleGuard><AudioPlayerProvider>
                <TasksLayout><TasksDefaultRedirect /></TasksLayout>
              </AudioPlayerProvider></TasksModuleGuard></TenantProtectedRoute>
            } />

            {/* ========== MÓDULOS RETENÇÃO E JURÍDICO ==========
                Reaproveitam 100% das telas de Tarefas. A separação dos dados é
                feita pelo enum task_type (normal | retention | legal), que o
                frontend deriva do prefixo da rota via useTaskModuleType(). */}
            {/* --- Retenção --- */}
            <Route path="/tenant/retention/dashboard" element={
              <TenantProtectedRoute><TasksModuleGuard submodule="retention"><AudioPlayerProvider>
                <PermissionGuard menuKey="retention_dashboard"><TasksLayout><TasksDashboard /></TasksLayout></PermissionGuard>
              </AudioPlayerProvider></TasksModuleGuard></TenantProtectedRoute>
            } />
            <Route path="/tenant/retention/list" element={
              <TenantProtectedRoute><TasksModuleGuard submodule="retention"><AudioPlayerProvider>
                <PermissionGuard menuKey="retention_list"><TasksLayout><TaskList /></TasksLayout></PermissionGuard>
              </AudioPlayerProvider></TasksModuleGuard></TenantProtectedRoute>
            } />
            <Route path="/tenant/retention/kanban" element={
              <TenantProtectedRoute><TasksModuleGuard submodule="retention"><AudioPlayerProvider>
                <PermissionGuard menuKey="retention_kanban"><TasksLayout><TaskKanbanPage /></TasksLayout></PermissionGuard>
              </AudioPlayerProvider></TasksModuleGuard></TenantProtectedRoute>
            } />
            <Route path="/tenant/retention/categories" element={
              <TenantProtectedRoute><TasksModuleGuard submodule="retention"><AudioPlayerProvider>
                <PermissionGuard menuKey="retention_categories"><TasksLayout><TaskCategories /></TasksLayout></PermissionGuard>
              </AudioPlayerProvider></TasksModuleGuard></TenantProtectedRoute>
            } />
            <Route path="/tenant/retention/phases" element={
              <TenantProtectedRoute><TasksModuleGuard submodule="retention"><AudioPlayerProvider>
                <PermissionGuard menuKey="retention_phases"><TasksLayout><TaskPhases /></TasksLayout></PermissionGuard>
              </AudioPlayerProvider></TasksModuleGuard></TenantProtectedRoute>
            } />
            <Route path="/tenant/retention/classifications" element={
              <TenantProtectedRoute><TasksModuleGuard submodule="retention"><AudioPlayerProvider>
                <PermissionGuard menuKey="retention_classifications"><TasksLayout><TaskClassifications /></TasksLayout></PermissionGuard>
              </AudioPlayerProvider></TasksModuleGuard></TenantProtectedRoute>
            } />
            {/* Motivos de conclusão (Retenção) */}
            <Route path="/tenant/retention/completion-reasons" element={
              <TenantProtectedRoute><TasksModuleGuard submodule="retention"><AudioPlayerProvider>
                <PermissionGuard menuKey="retention_completion_reasons"><TasksLayout><TaskCompletionReasons /></TasksLayout></PermissionGuard>
              </AudioPlayerProvider></TasksModuleGuard></TenantProtectedRoute>
            } />
            {/* Permissões por fase (Retenção) — central unificada já na subaba correta */}
            <Route
              path="/tenant/retention/phase-permissions"
              element={<Navigate to="/tenant/permissions?tab=task-phases&type=retention" replace />}
            />
            <Route path="/tenant/retention" element={<Navigate to="/tenant/retention/kanban" replace />} />

            {/* --- Jurídico --- */}
            <Route path="/tenant/legal/dashboard" element={
              <TenantProtectedRoute><TasksModuleGuard submodule="legal"><AudioPlayerProvider>
                <PermissionGuard menuKey="legal_dashboard"><TasksLayout><TasksDashboard /></TasksLayout></PermissionGuard>
              </AudioPlayerProvider></TasksModuleGuard></TenantProtectedRoute>
            } />
            <Route path="/tenant/legal/list" element={
              <TenantProtectedRoute><TasksModuleGuard submodule="legal"><AudioPlayerProvider>
                <PermissionGuard menuKey="legal_list"><TasksLayout><TaskList /></TasksLayout></PermissionGuard>
              </AudioPlayerProvider></TasksModuleGuard></TenantProtectedRoute>
            } />
            <Route path="/tenant/legal/kanban" element={
              <TenantProtectedRoute><TasksModuleGuard submodule="legal"><AudioPlayerProvider>
                <PermissionGuard menuKey="legal_kanban"><TasksLayout><TaskKanbanPage /></TasksLayout></PermissionGuard>
              </AudioPlayerProvider></TasksModuleGuard></TenantProtectedRoute>
            } />
            <Route path="/tenant/legal/categories" element={
              <TenantProtectedRoute><TasksModuleGuard submodule="legal"><AudioPlayerProvider>
                <PermissionGuard menuKey="legal_categories"><TasksLayout><TaskCategories /></TasksLayout></PermissionGuard>
              </AudioPlayerProvider></TasksModuleGuard></TenantProtectedRoute>
            } />
            <Route path="/tenant/legal/phases" element={
              <TenantProtectedRoute><TasksModuleGuard submodule="legal"><AudioPlayerProvider>
                <PermissionGuard menuKey="legal_phases"><TasksLayout><TaskPhases /></TasksLayout></PermissionGuard>
              </AudioPlayerProvider></TasksModuleGuard></TenantProtectedRoute>
            } />
            <Route path="/tenant/legal/classifications" element={
              <TenantProtectedRoute><TasksModuleGuard submodule="legal"><AudioPlayerProvider>
                <PermissionGuard menuKey="legal_classifications"><TasksLayout><TaskClassifications /></TasksLayout></PermissionGuard>
              </AudioPlayerProvider></TasksModuleGuard></TenantProtectedRoute>
            } />
            {/* Motivos de conclusão (Jurídico) */}
            <Route path="/tenant/legal/completion-reasons" element={
              <TenantProtectedRoute><TasksModuleGuard submodule="legal"><AudioPlayerProvider>
                <PermissionGuard menuKey="legal_completion_reasons"><TasksLayout><TaskCompletionReasons /></TasksLayout></PermissionGuard>
              </AudioPlayerProvider></TasksModuleGuard></TenantProtectedRoute>
            } />
            {/* Permissões por fase (Jurídico) — central unificada já na subaba correta */}
            <Route
              path="/tenant/legal/phase-permissions"
              element={<Navigate to="/tenant/permissions?tab=task-phases&type=legal" replace />}
            />
            <Route path="/tenant/legal" element={<Navigate to="/tenant/legal/kanban" replace />} />


            {/* ========== MÓDULO VEÍCULOS ========== */}
            {/* 2026-10-03: Dashboard do módulo Veículos (aba Geral) */}
            <Route path="/tenant/vehicles/dashboard" element={
              <TenantProtectedRoute><AudioPlayerProvider>
                <PermissionGuard menuKey="vehicles_list"><ModularLayout><VehiclesDashboardPage /></ModularLayout></PermissionGuard>
              </AudioPlayerProvider></TenantProtectedRoute>
            } />
            {/* Listagem geral e cadastro de categorias de veículo do tenant. */}
            <Route path="/tenant/vehicles/list" element={
              <TenantProtectedRoute><AudioPlayerProvider>
                <PermissionGuard menuKey="vehicles_list"><ModularLayout><VehiclesListPage /></ModularLayout></PermissionGuard>
              </AudioPlayerProvider></TenantProtectedRoute>
            } />
            {/* Vistoria: central de vistorias (lista e kanban, cada um com sua permissão) */}
            <Route path="/tenant/vehicles/inspections" element={
              <TenantProtectedRoute><AudioPlayerProvider>
                <PermissionGuard menuKey="vehicles_inspections"><ModularLayout><VehicleInspectionsPage /></ModularLayout></PermissionGuard>
              </AudioPlayerProvider></TenantProtectedRoute>
            } />
            <Route path="/tenant/vehicles/inspections/kanban" element={
              <TenantProtectedRoute><AudioPlayerProvider>
                <PermissionGuard menuKey="vehicles_inspections_kanban"><ModularLayout><VehicleInspectionsPage /></ModularLayout></PermissionGuard>
              </AudioPlayerProvider></TenantProtectedRoute>
            } />
            <Route path="/tenant/vehicles/map" element={
              <TenantProtectedRoute><AudioPlayerProvider>
                <PermissionGuard menuKey="vehicles_list"><ModularLayout><VehicleMapPage /></ModularLayout></PermissionGuard>
              </AudioPlayerProvider></TenantProtectedRoute>
            } />
            <Route path="/tenant/vehicles/categories" element={
              <TenantProtectedRoute><AudioPlayerProvider>
                <PermissionGuard menuKey="vehicles_categories"><ModularLayout><VehicleCategoriesPage /></ModularLayout></PermissionGuard>
              </AudioPlayerProvider></TenantProtectedRoute>
            } />
            {/* 2026-10-02: Status de veículo (reutiliza a permissão de Categorias) */}
            <Route path="/tenant/vehicles/statuses" element={
              <TenantProtectedRoute><AudioPlayerProvider>
                <PermissionGuard menuKey="vehicles_categories"><ModularLayout><VehicleStatusesPage /></ModularLayout></PermissionGuard>
              </AudioPlayerProvider></TenantProtectedRoute>
            } />
            {/* 2026-10-06: Pessoas (fornecedor/fabricante/prestador) */}
            <Route path="/tenant/persons" element={
              <TenantProtectedRoute><AudioPlayerProvider>
                <PermissionGuard menuKey="persons"><ModularLayout><PersonsPage /></ModularLayout></PermissionGuard>
              </AudioPlayerProvider></TenantProtectedRoute>
            } />
            {/* 2026-10-06: submenus especialistas de Pessoas (independentes do Estoque) */}
            <Route path="/tenant/persons/:role" element={
              <TenantProtectedRoute><AudioPlayerProvider>
                <PersonsRoleGuard><ModularLayout><PersonsPage /></ModularLayout></PersonsRoleGuard>
              </AudioPlayerProvider></TenantProtectedRoute>
            } />
            {/* 2026-10-16: Logística — Kanban de Remessas (somente leitura) */}
            <Route path="/tenant/logistics/shipments/kanban" element={
              <TenantProtectedRoute><AudioPlayerProvider>
                <PermissionGuard menuKey="log_shipments"><ModularLayout><ShipmentsKanbanPage /></ModularLayout></PermissionGuard>
              </AudioPlayerProvider></TenantProtectedRoute>
            } />
            {/* 2026-10-16: Logística — Remessas (Fase 2) */}
            <Route path="/tenant/logistics/shipments" element={
              <TenantProtectedRoute><AudioPlayerProvider>
                <PermissionGuard menuKey="log_shipments"><ModularLayout><ShipmentsPage /></ModularLayout></PermissionGuard>
              </AudioPlayerProvider></TenantProtectedRoute>
            } />
            {/* 2026-10-08: Ordem de Serviço Fase 1 — rotas protegidas (telas reais nas próximas fases) */}
            {/* 2026-10-18: Dashboard do módulo O.S. (aba Visão Geral) */}
            <Route path="/tenant/service-orders/dashboard" element={
              <TenantProtectedRoute><AudioPlayerProvider>
                <ServiceOrderModuleGuard><PermissionGuard menuKey="os_list"><ModularLayout><ServiceOrdersDashboardPage /></ModularLayout></PermissionGuard></ServiceOrderModuleGuard>
              </AudioPlayerProvider></TenantProtectedRoute>
            } />
            <Route path="/tenant/service-orders/kanban" element={
              <TenantProtectedRoute><AudioPlayerProvider>
                <ServiceOrderModuleGuard><PermissionGuard menuKey="os_kanban"><ModularLayout><ServiceOrdersKanbanPage /></ModularLayout></PermissionGuard></ServiceOrderModuleGuard>
              </AudioPlayerProvider></TenantProtectedRoute>
            } />
            <Route path="/tenant/service-orders/list" element={
              <TenantProtectedRoute><AudioPlayerProvider>
                <ServiceOrderModuleGuard><PermissionGuard menuKey="os_list"><ModularLayout><ServiceOrdersListPage /></ModularLayout></PermissionGuard></ServiceOrderModuleGuard>
              </AudioPlayerProvider></TenantProtectedRoute>
            } />
            <Route path="/tenant/service-orders/categories" element={
              <TenantProtectedRoute><AudioPlayerProvider>
                <ServiceOrderModuleGuard><PermissionGuard menuKey="os_categories"><ModularLayout><TaskCategories /></ModularLayout></PermissionGuard></ServiceOrderModuleGuard>
              </AudioPlayerProvider></TenantProtectedRoute>
            } />
            <Route path="/tenant/service-orders/phases" element={
              <TenantProtectedRoute><AudioPlayerProvider>
                <ServiceOrderModuleGuard><PermissionGuard menuKey="os_phases"><ModularLayout><TaskPhases /></ModularLayout></PermissionGuard></ServiceOrderModuleGuard>
              </AudioPlayerProvider></TenantProtectedRoute>
            } />
            {/* 2026-10-09: Permissões por fase (OS) — central unificada já na subaba correta,
                mesmo padrão dos módulos Tarefas/Retenção/Jurídico (preserva bookmarks). */}
            <Route
              path="/tenant/service-orders/phase-permissions"
              element={<Navigate to="/tenant/permissions?tab=os-phases" replace />}
            />
            <Route path="/tenant/service-orders/service-types" element={
              <TenantProtectedRoute><AudioPlayerProvider>
                <ServiceOrderModuleGuard><PermissionGuard menuKey="os_service_types"><ModularLayout><ServiceTypesPage /></ModularLayout></PermissionGuard></ServiceOrderModuleGuard>
              </AudioPlayerProvider></TenantProtectedRoute>
            } />
            {/* 2026-10-06: Estoque Fases 4/5 — visão geral e movimentações */}
            <Route path="/tenant/stock/overview" element={
              <TenantProtectedRoute><AudioPlayerProvider>
                <StockModuleGuard><PermissionGuard menuKey="stock_overview"><ModularLayout><StockOverviewPage /></ModularLayout></PermissionGuard></StockModuleGuard>
              </AudioPlayerProvider></TenantProtectedRoute>
            } />
            <Route path="/tenant/stock/movements" element={
              <TenantProtectedRoute><AudioPlayerProvider>
                <StockModuleGuard><PermissionGuard menuKey="stock_movements"><ModularLayout><StockMovementsPage /></ModularLayout></PermissionGuard></StockModuleGuard>
              </AudioPlayerProvider></TenantProtectedRoute>
            } />
            {/* 2026-10-06: Estoque Fase 3 — rastreadores, chips e kits */}
            <Route path="/tenant/stock/trackers" element={
              <TenantProtectedRoute><AudioPlayerProvider>
                <StockModuleGuard><PermissionGuard menuKey="stock_trackers"><ModularLayout><StockItemsPage kind="tracker" /></ModularLayout></PermissionGuard></StockModuleGuard>
              </AudioPlayerProvider></TenantProtectedRoute>
            } />
            <Route path="/tenant/stock/chips" element={
              <TenantProtectedRoute><AudioPlayerProvider>
                <StockModuleGuard><PermissionGuard menuKey="stock_chips"><ModularLayout><StockItemsPage kind="chip" /></ModularLayout></PermissionGuard></StockModuleGuard>
              </AudioPlayerProvider></TenantProtectedRoute>
            } />
            <Route path="/tenant/stock/kits" element={
              <TenantProtectedRoute><AudioPlayerProvider>
                <StockModuleGuard><PermissionGuard menuKey="stock_kits"><ModularLayout><StockItemsPage kind="kit" /></ModularLayout></PermissionGuard></StockModuleGuard>
              </AudioPlayerProvider></TenantProtectedRoute>
            } />
            {/* 2026-10-06: Estoque — endereços e modelos de rastreador */}
            <Route path="/tenant/stock/locations" element={
              <TenantProtectedRoute><AudioPlayerProvider>
                <StockModuleGuard><PermissionGuard menuKey="stock_locations"><ModularLayout><StockLocationsPage /></ModularLayout></PermissionGuard></StockModuleGuard>
              </AudioPlayerProvider></TenantProtectedRoute>
            } />
            <Route path="/tenant/stock/models" element={
              <TenantProtectedRoute><AudioPlayerProvider>
                <StockModuleGuard><PermissionGuard menuKey="stock_models"><ModularLayout><TrackerModelsPage /></ModularLayout></PermissionGuard></StockModuleGuard>
              </AudioPlayerProvider></TenantProtectedRoute>
            } />
            {/* 2026-10-11: Gestão Rastreador — Homologação */}
            <Route path="/tenant/stock/homologations" element={
              <TenantProtectedRoute><AudioPlayerProvider>
                <StockModuleGuard><PermissionGuard menuKey="stock_homologations"><ModularLayout><StockHomologationsPage /></ModularLayout></PermissionGuard></StockModuleGuard>
              </AudioPlayerProvider></TenantProtectedRoute>
            } />
            {/* 2026-10-11: Gestão Rastreador — Plataformas de rastreamento */}
            <Route path="/tenant/stock/platforms" element={
              <TenantProtectedRoute><AudioPlayerProvider>
                <StockModuleGuard><PermissionGuard menuKey="stock_platforms"><ModularLayout><TrackingPlatformsPage /></ModularLayout></PermissionGuard></StockModuleGuard>
              </AudioPlayerProvider></TenantProtectedRoute>
            } />
            {/* 2026-10-07: Gestão de Chip — Planos de dados */}
            <Route path="/tenant/stock/data-plans" element={
              <TenantProtectedRoute><AudioPlayerProvider>
                <StockModuleGuard><PermissionGuard menuKey="stock_data_plans"><ModularLayout><StockDataPlansPage /></ModularLayout></PermissionGuard></StockModuleGuard>
              </AudioPlayerProvider></TenantProtectedRoute>
            } />
            {/* 2026-10-14: Gestão de Chip — Cancelamento em lote */}
            <Route path="/tenant/stock/chip-cancellations" element={
              <TenantProtectedRoute><AudioPlayerProvider>
                <StockModuleGuard><PermissionGuard menuKey="stock_chip_cancellations"><ModularLayout><StockChipCancellationsPage /></ModularLayout></PermissionGuard></StockModuleGuard>
              </AudioPlayerProvider></TenantProtectedRoute>
            } />
            <Route path="/tenant/vehicles" element={<Navigate to="/tenant/vehicles/list" replace />} />

            {/* ===== MÓDULO WORKFLOW (motor de orquestração 2.0) ===== */}
            {/* O interruptor do módulo vive em Configurações › Workflow, por isso a
                rota é protegida pelo guard da flag `workflow_enabled`. */}
            <Route path="/tenant/workflow" element={
              <TenantProtectedRoute><AudioPlayerProvider>
                <WorkflowModuleGuard><ModularLayout><WorkflowHomePage /></ModularLayout></WorkflowModuleGuard>
              </AudioPlayerProvider></TenantProtectedRoute>
            } />
            {/* 2026-10-06: submenus do Workflow, cada um com permissão própria.
                Declarados antes de /:definitionId para não cair no construtor. */}
            <Route path="/tenant/workflow/pending" element={
              <TenantProtectedRoute><AudioPlayerProvider>
                <WorkflowModuleGuard><PermissionGuard menuKey="workflow_pending"><ModularLayout><WorkflowPendingPage /></ModularLayout></PermissionGuard></WorkflowModuleGuard>
              </AudioPlayerProvider></TenantProtectedRoute>
            } />
            <Route path="/tenant/workflow/executions" element={
              <TenantProtectedRoute><AudioPlayerProvider>
                <WorkflowModuleGuard><PermissionGuard menuKey="workflow_executions"><ModularLayout><WorkflowExecutionsPage /></ModularLayout></PermissionGuard></WorkflowModuleGuard>
              </AudioPlayerProvider></TenantProtectedRoute>
            } />
            <Route path="/tenant/workflow/errors" element={
              <TenantProtectedRoute><AudioPlayerProvider>
                <WorkflowModuleGuard><PermissionGuard menuKey="workflow_errors"><ModularLayout><WorkflowErrorsPage /></ModularLayout></PermissionGuard></WorkflowModuleGuard>
              </AudioPlayerProvider></TenantProtectedRoute>
            } />
            {/* Construtor visual do processo (BPMN v2). Renderiza SEM o layout
                modular para ocupar a tela inteira, como o construtor de Fluxos. */}
            <Route path="/tenant/workflow/:definitionId" element={
              <TenantProtectedRoute><AudioPlayerProvider>
                <WorkflowModuleGuard><WorkflowBuilderPage /></WorkflowModuleGuard>
              </AudioPlayerProvider></TenantProtectedRoute>
            } />






            {/* ===== MÓDULO INTELIGÊNCIA DE DADOS (Integration Hub — Fase 1) ===== */}
            <Route path="/tenant/data/connections" element={
              <TenantProtectedRoute><AudioPlayerProvider>
                <PermissionGuard menuKey="data_connections"><ModularLayout><DataConnectionsPage /></ModularLayout></PermissionGuard>
              </AudioPlayerProvider></TenantProtectedRoute>
            } />
            {/* Página do fornecedor: gerencia N conexões daquele app */}
            <Route path="/tenant/data/connections/:providerSlug" element={
              <TenantProtectedRoute><AudioPlayerProvider>
                <PermissionGuard menuKey="data_connections"><ModularLayout><DataProviderPage /></ModularLayout></PermissionGuard>
              </AudioPlayerProvider></TenantProtectedRoute>
            } />
            {/* Dados Externos (Fase 2) — datasets e histórico de importações */}
            <Route path="/tenant/data/datasets" element={
              <TenantProtectedRoute><AudioPlayerProvider>
                <PermissionGuard menuKey="data_datasets"><ModularLayout><DataDatasetsPage /></ModularLayout></PermissionGuard>
              </AudioPlayerProvider></TenantProtectedRoute>
            } />
            <Route path="/tenant/data/sync-history" element={
              <TenantProtectedRoute><AudioPlayerProvider>
                <PermissionGuard menuKey="data_sync_history"><ModularLayout><DataSyncHistoryPage /></ModularLayout></PermissionGuard>
              </AudioPlayerProvider></TenantProtectedRoute>
            } />
            {/* Ações em lote (Fase 4) — acompanhamento dos lotes criados no Explorador */}
            <Route path="/tenant/data/actions" element={
              <TenantProtectedRoute><AudioPlayerProvider>
                <PermissionGuard menuKey="data_explorer"><ModularLayout><DataActionBatchesPage /></ModularLayout></PermissionGuard>
              </AudioPlayerProvider></TenantProtectedRoute>
            } />

            {/* Explorador de dados (Fase 3) — grade dinâmica dos registros importados */}
            <Route path="/tenant/data/explorer" element={
              <TenantProtectedRoute><AudioPlayerProvider>
                <PermissionGuard menuKey="data_explorer"><ModularLayout><DataExplorerPage /></ModularLayout></PermissionGuard>
              </AudioPlayerProvider></TenantProtectedRoute>
            } />
            <Route path="/tenant/data/explorer/:datasetId" element={
              <TenantProtectedRoute><AudioPlayerProvider>
                <PermissionGuard menuKey="data_explorer"><ModularLayout><DataExplorerPage /></ModularLayout></PermissionGuard>
              </AudioPlayerProvider></TenantProtectedRoute>
            } />
            <Route path="/tenant/data" element={<Navigate to="/tenant/data/connections" replace />} />

            {/* ========== MÓDULO INDICAÇÃO (Fase 5 — Bloco 5A → 5F) ========== */}
            {/* Cada rota é protegida individualmente por PermissionGuard com
                chave referral_* específica. Admins (root_admin/tenant_admin)
                bypassam o canView() e mantêm acesso total. Configurações é
                restrita a admin (requireAdmin). */}
            <Route path="/tenant/indicacao" element={
              <TenantProtectedRoute><ReferralModuleGuard><PermissionGuard menuKey="referral_dashboard"><AudioPlayerProvider>
                <ReferralLayout><ReferralDashboard /></ReferralLayout>
              </AudioPlayerProvider></PermissionGuard></ReferralModuleGuard></TenantProtectedRoute>
            } />
            <Route path="/tenant/indicacao/programas" element={
              <TenantProtectedRoute><ReferralModuleGuard><PermissionGuard menuKey="referral_programs"><AudioPlayerProvider>
                <ReferralLayout><ReferralPrograms /></ReferralLayout>
              </AudioPlayerProvider></PermissionGuard></ReferralModuleGuard></TenantProtectedRoute>
            } />
            <Route path="/tenant/indicacao/programas/:programId" element={
              <TenantProtectedRoute><ReferralModuleGuard><PermissionGuard menuKey="referral_programs"><AudioPlayerProvider>
                <ReferralLayout><ReferralProgramDetail /></ReferralLayout>
              </AudioPlayerProvider></PermissionGuard></ReferralModuleGuard></TenantProtectedRoute>
            } />
            <Route path="/tenant/indicacao/campanhas" element={
              <TenantProtectedRoute><ReferralModuleGuard><PermissionGuard menuKey="referral_campaigns"><AudioPlayerProvider>
                <ReferralLayout><ReferralCampaigns /></ReferralLayout>
              </AudioPlayerProvider></PermissionGuard></ReferralModuleGuard></TenantProtectedRoute>
            } />
            {/* Rota /tenant/indicacao/configuracoes removida — campos
                consolidados no modal de Programa (aba "Branding & Termos").
                A entrada "Configurações" da sidebar de Indicação agora aponta
                para /tenant/settings (Configurações Gerais do tenant). */}
            {/* Bloco 5D — Afiliados (lista + moderação) e Materiais (CRUD admin) */}
            <Route path="/tenant/indicacao/afiliados" element={
              <TenantProtectedRoute><ReferralModuleGuard><PermissionGuard menuKey="referral_affiliates"><AudioPlayerProvider>
                <ReferralLayout><ReferralAffiliates /></ReferralLayout>
              </AudioPlayerProvider></PermissionGuard></ReferralModuleGuard></TenantProtectedRoute>
            } />
            {/* Indicações — listagem administrativa centralizada (v1.0.0) */}
            <Route path="/tenant/indicacao/indicacoes" element={
              <TenantProtectedRoute><ReferralModuleGuard><PermissionGuard menuKey="referral_referrals"><AudioPlayerProvider>
                <ReferralLayout><ReferralReferralsAdmin /></ReferralLayout>
              </AudioPlayerProvider></PermissionGuard></ReferralModuleGuard></TenantProtectedRoute>
            } />
            <Route path="/tenant/indicacao/materiais" element={
              <TenantProtectedRoute><ReferralModuleGuard><PermissionGuard menuKey="referral_materials"><AudioPlayerProvider>
                <ReferralLayout><ReferralMaterials /></ReferralLayout>
              </AudioPlayerProvider></PermissionGuard></ReferralModuleGuard></TenantProtectedRoute>
            } />
            {/* Bloco 5E — Comissões e Pagamentos (visão admin do tenant) */}
            <Route path="/tenant/indicacao/comissoes" element={
              <TenantProtectedRoute><ReferralModuleGuard><PermissionGuard menuKey="referral_commissions"><AudioPlayerProvider>
                <ReferralLayout><ReferralCommissions /></ReferralLayout>
              </AudioPlayerProvider></PermissionGuard></ReferralModuleGuard></TenantProtectedRoute>
            } />
            <Route path="/tenant/indicacao/pagamentos" element={
              <TenantProtectedRoute><ReferralModuleGuard><PermissionGuard menuKey="referral_payouts"><AudioPlayerProvider>
                <ReferralLayout><ReferralPayouts /></ReferralLayout>
              </AudioPlayerProvider></PermissionGuard></ReferralModuleGuard></TenantProtectedRoute>
            } />
            {/* Bloco 8.7 — Auditoria (admin) */}
            <Route path="/tenant/indicacao/auditoria" element={
              <TenantProtectedRoute><ReferralModuleGuard><PermissionGuard menuKey="referral_dashboard" requireAdmin><AudioPlayerProvider>
                <ReferralLayout><ReferralAuditLog /></ReferralLayout>
              </AudioPlayerProvider></PermissionGuard></ReferralModuleGuard></TenantProtectedRoute>
            } />
            {/* Alias: Modelos de Contrato dentro do contexto do módulo Indicação.
                Reaproveita a página CrmContractTemplates, mas mantém o
                ReferralLayout (sidebar + módulo no header) para preservar
                o contexto mental do usuário. */}
            <Route path="/tenant/indicacao/modelos-contrato" element={
              <TenantProtectedRoute><ReferralModuleGuard><PermissionGuard menuKey="crm_contract_templates"><AudioPlayerProvider>
                <ReferralLayout><CrmContractTemplates /></ReferralLayout>
              </AudioPlayerProvider></PermissionGuard></ReferralModuleGuard></TenantProtectedRoute>
            } />

            {/* Rota pública do chat do fluxo (sem autenticação) - inclui tenant_slug para namespace */}
            <Route path="/chat/:tenantSlug/:flowSlug" element={<PublicFlowChat />} />
            
            {/* Rota pública de agendamento online (sem autenticação) - inclui slug do tenant para unicidade */}
            <Route path="/schedule/:tenantSlug/:slug" element={<PublicScheduling />} />
            
            {/* Rota pública de cancelamento de agendamento (sem autenticação) */}
            <Route path="/schedule/:tenantSlug/cancel/:token" element={<PublicSchedulingCancel />} />

            {/* Rota pública de redirecionamento de links de indicação (Fase 4 — item 4.9) */}
            <Route path="/r/:slug" element={<AffiliateRedirectPage />} />

            {/* Checkout Transparente — rota pública /c/:slug (Fase 4 Sub-fase A) */}
            <Route path="/c/:slug" element={<CheckoutPublic />} />
            {/* Aceites Fase 4: verificação pública mínima de comprovantes */}
            <Route path="/verificar-aceite" element={<VerifyAcceptancePage />} />
            {/* Vistorias: verificação pública do dossiê sem envio do arquivo */}
            <Route path="/verificar-vistoria" element={<VerifyChecklistReportPage />} />

            {/* Propostas — landing pública /p/:token (Fase 6 do módulo de Propostas) */}
            <Route path="/p/:token" element={<PublicProposal />} />
            {/* Vistoria — link externo /v/:token (acesso após confirmar CPF) */}
            <Route path="/v/:token" element={<PublicInspectionPage />} />

            {/* Studio — prévia assinada e landing page publicada (sem autenticação).
                O conteúdo vem da função studio-public-page e é exibido em moldura
                isolada, pois o endereço técnico da função entrega texto puro. */}
            <Route path="/studio-preview/:token" element={<StudioPublicFrame />} />
            <Route path="/lp/*" element={<StudioPublicFrame />} />



            {/* =====================================================================
                Módulo Afiliados (Fase 2) — Auth & Onboarding
                Rotas públicas (login/cadastro) e privadas (painel) isoladas
                do sistema interno. Todas envolvidas pelo AffiliateAuthProvider
                para que o hook useAffiliateAuth funcione em qualquer rota /afiliado/*.
            ===================================================================== */}
            {/* Login global (legado) — mantido para retrocompatibilidade */}
            <Route
              path="/afiliado/login"
              element={
                <AffiliateAuthProvider>
                  <AffiliateLogin />
                </AffiliateAuthProvider>
              }
            />
            {/* Login canônico por tenant — habilita branding personalizado por URL */}
            <Route
              path="/afiliado/:tenantSlug/login"
              element={
                <AffiliateAuthProvider>
                  <AffiliateLogin />
                </AffiliateAuthProvider>
              }
            />
            {/* Recuperação de senha — públicas (global + por tenant) */}
            <Route
              path="/afiliado/esqueci-senha"
              element={
                <AffiliateAuthProvider>
                  <AffiliateForgotPassword />
                </AffiliateAuthProvider>
              }
            />
            <Route
              path="/afiliado/:tenantSlug/esqueci-senha"
              element={
                <AffiliateAuthProvider>
                  <AffiliateForgotPassword />
                </AffiliateAuthProvider>
              }
            />
            <Route
              path="/afiliado/redefinir-senha"
              element={
                <AffiliateAuthProvider>
                  <AffiliateResetPassword />
                </AffiliateAuthProvider>
              }
            />
            <Route
              path="/afiliado/:tenantSlug/redefinir-senha"
              element={
                <AffiliateAuthProvider>
                  <AffiliateResetPassword />
                </AffiliateAuthProvider>
              }
            />
            <Route
              path="/afiliado/cadastro/:tenantSlug/:programSlug"
              element={
                <AffiliateAuthProvider>
                  <AffiliateSignup />
                </AffiliateAuthProvider>
              }
            />
            {/* Convite privado (invite_only) — resolver invisível:
                valida o token e redireciona para login ou cadastro,
                repassando ?invite=<token> para vincular ao aceitar.
                Rota canônica por tenant + fallback legado global. */}
            <Route
              path="/afiliado/:tenantSlug/convite/:token"
              element={
                <AffiliateAuthProvider>
                  <AffiliateInviteResolver />
                </AffiliateAuthProvider>
              }
            />
            <Route
              path="/afiliado/convite/:token"
              element={
                <AffiliateAuthProvider>
                  <AffiliateInviteResolver />
                </AffiliateAuthProvider>
              }
            />
            {/* Aceite de convite por usuário autenticado, inclusive quando ainda
                não existe registro em affiliates. A própria página exige sessão
                e a RPC valida token, e-mail, status e expiração do convite. */}
            <Route
              path="/afiliado/:tenantSlug/aceitar-convite/:token"
              element={
                <AffiliateAuthProvider>
                  <AffiliateAcceptInvite />
                </AffiliateAuthProvider>
              }
            />
            <Route
              path="/afiliado/aceitar-convite/:token"
              element={
                <AffiliateAuthProvider>
                  <AffiliateAcceptInvite />
                </AffiliateAuthProvider>
              }
            />
            {/* Resolver do cadastro: descobre o programa PADRÃO do tenant
                e redireciona para a landing /afiliado/cadastro/:tenantSlug/:programSlug.
                Funciona tanto na URL global quanto na canônica por tenant. */}
            <Route
              path="/afiliado/cadastro"
              element={
                <AffiliateAuthProvider>
                  <AffiliateSignupResolver />
                </AffiliateAuthProvider>
              }
            />
            <Route
              path="/afiliado/:tenantSlug/cadastro"
              element={
                <AffiliateAuthProvider>
                  <AffiliateSignupResolver />
                </AffiliateAuthProvider>
              }
            />
            <Route
              path="/afiliado/aguardando-aprovacao"
              element={
                <AffiliateAuthProvider>
                  <AffiliateProtectedRoute allowPending>
                    <AffiliateAwaitingApproval />
                  </AffiliateProtectedRoute>
                </AffiliateAuthProvider>
              }
            />
            <Route
              path="/afiliado/:tenantSlug/aguardando-aprovacao"
              element={
                <AffiliateAuthProvider>
                  <AffiliateProtectedRoute allowPending>
                    <AffiliateAwaitingApproval />
                  </AffiliateProtectedRoute>
                </AffiliateAuthProvider>
              }
            />
            {/* Plano v2 — Multi-Tenant: vincular usuário logado a um novo programa */}
            <Route
              path="/afiliado/vincular-programa/:tenantSlug/:programSlug"
              element={
                <AffiliateAuthProvider>
                  <AffiliateLinkProgram />
                </AffiliateAuthProvider>
              }
            />
            {/* Plano v2 — Multi-Tenant: seleção de conta quando há múltiplas */}
            <Route
              path="/afiliado/selecionar-conta"
              element={
                <AffiliateAuthProvider>
                  <AffiliateSelectAccount />
                </AffiliateAuthProvider>
              }
            />

            {/* =====================================================================
                Fase 3 — Bloco 3A: Painel do Afiliado (rotas privadas com layout)
                Todas as rotas filhas exigem affiliate.status='active'.
            ===================================================================== */}
            <Route
              path="/afiliado"
              element={
                <AffiliateAuthProvider>
                  <AffiliateProtectedRoute>
                    <AffiliateLayout />
                  </AffiliateProtectedRoute>
                </AffiliateAuthProvider>
              }
            >
              <Route index element={<AffiliateDashboard />} />
              <Route path="links" element={<AffiliateLinks />} />
              <Route path="indicacoes" element={<AffiliateReferrals />} />
              <Route path="comissoes" element={<AffiliateCommissions />} />
              <Route path="saques" element={<AffiliatePayouts />} />
              <Route path="materiais" element={<AffiliateMaterials />} />
              <Route path="perfil" element={<AffiliateProfile />} />
            </Route>

            {/* =====================================================================
                Fase 4 — Extranet KB
                Portal externo isolado por tenant (/extranet/:tenantSlug/...).
                Todas as rotas envolvidas pelo ExtranetAuthProvider (via wrapper).
            ===================================================================== */}
            {/* Rotas públicas do fluxo de autenticação — cada página instancia
                seu próprio ExtranetAuthProvider(tenantSlug) por já ler useParams. */}
            {/* 2026-10-12: Portal do Prestador (público, sessão própria) */}
            <Route path="/prestador/:tenantSlug" element={<ProviderPortalHome />} />
            <Route path="/prestador/:tenantSlug/login" element={<ProviderPortalLogin />} />
            <Route path="/prestador/:tenantSlug/ativar" element={<ProviderPortalActivate />} />
            {/* 2026-10-13: vistoria dentro do app do prestador (sessão emitida pelo servidor) */}
            <Route path="/prestador/:tenantSlug/vistoria/:token" element={<ProviderInspectionPage />} />
            <Route path="/extranet/:tenantSlug/login" element={<ExtranetLogin />} />
            <Route path="/extranet/:tenantSlug/cadastro" element={<ExtranetSignup />} />
            <Route path="/extranet/:tenantSlug/esqueci-senha" element={<ExtranetForgotPassword />} />
            <Route path="/extranet/:tenantSlug/redefinir-senha" element={<ExtranetResetPassword />} />
            <Route path="/extranet/:tenantSlug/aguardando" element={<ExtranetAwaitingApproval />} />
            <Route path="/extranet/:tenantSlug/confirme-email" element={<ExtranetConfirmEmail />} />


            {/* Rotas privadas do portal (via wrapper que injeta o provider). */}
            <Route path="/extranet/:tenantSlug" element={<ExtranetAuthProviderRoute />}>
              <Route
                element={
                  <ExtranetProtectedRoute>
                    <ExtranetLayout />
                  </ExtranetProtectedRoute>
                }
              >
                <Route index element={<ExtranetHome />} />
                <Route path="espacos/:spaceSlug" element={<ExtranetSpacePage />} />
                <Route path="espacos/:spaceSlug/:pageSlug" element={<ExtranetPagePage />} />
                <Route path="perfil" element={<ExtranetProfile />} />
                <Route path="meus-acessos" element={<ExtranetMyAccesses />} />
              </Route>
            </Route>

            <Route path="*" element={<NotFound />} />
          </Routes>
          {/* Widget de Suporte persistente - UMA instância para toda a sessão */}
          <SupportWidget />
          </QuickCreateModalsProvider>
          </NotificationsRealtimeProvider>
          </TasksQuickProvider>
        </BrowserRouter>
            </WorkflowGuardNoticeProvider>
        </TooltipProvider>
        </AuthProvider>
      </ThemeProvider>
    </QueryClientProvider>
  </SentryErrorBoundary>
);

export default App;
